import { useWattelEM } from '../hooks/useWattelEM';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import type { DeviceOverview, ControlLevel, ControlMode } from '../api/wattelEM';
import { architectureApi, type CatalogDevice, type MeterLatest, type SessionEnergy, type InstallationData } from '../api/architecture';
import {
  EnergyOverTimeChart,
  ChartTimeFilter,
  ScheduleStatusCard,
  SchedulePlannerModal,
  useDeviceSchedule,
  useScheduleExecutor,
} from '../components/wattelEM';
import type { TimeFilterValue } from '../components/wattelEM';
import { wattelEMApi } from '../api/wattelEM';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { displayDefaultName } from '../i18n/defaultNames';
import type { TranslationKey } from '../i18n/translations';

type TFn = ReturnType<typeof useI18n>['t'];

// ============================================
// Helper
// ============================================

function fmt(v: string | number | null | undefined, decimals = 2): string {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return isNaN(n) ? '—' : n.toFixed(decimals);
}

function timeAgo(dateStr: string | null, t: TFn): string {
  if (!dateStr) return t('eq_never');
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60_000) return t('eq_agoFewSeconds');
  if (diff < 3_600_000) return t('eq_agoMin', { n: Math.floor(diff / 60_000) });
  if (diff < 86_400_000) return t('eq_agoHours', { n: Math.floor(diff / 3_600_000) });
  return t('eq_agoDays', { n: Math.floor(diff / 86_400_000) });
}

// Libellé d'un type de point électrique (valeur interne conservée, seul l'affichage est traduit).
const POINT_TYPES = ['prise', 'lumiere', 'chauffage', 'clim', 'chauffeeau', 'four', 'lave-linge', 'voiture'];
function pointTypeLabel(type: string | null, t: TFn): string {
  const v = type || 'prise';
  return POINT_TYPES.includes(v) ? t(`eq_pointType_${v}` as TranslationKey) : v;
}

// Display alias helper (uses shared DEVICE_DISPLAY_NAMES map)
import { displaySn } from '../utils/deviceDisplay';
import { classifyPowerFactor } from '../utils/powerFactor';
import { startTransaction, stopTransaction, getChargingSessionStatus, type ChargingSessionStatus } from '../api/deviceService';
import { otaApi } from '../api/ota';
import { getOrgBornes } from '../api/bornesService';
import {
  getBorneSession,
  startBorneSession,
  endBorneSession,
  updateBorneSession,
  computeLiveBorne,
  BORNE_SESSION_EVENT,
} from '../utils/borneSession';

// ============================================
// Sub-components
// ============================================

// Card for catalog devices (WattelGo, WattelWall, etc.) — same style as DeviceCard
function CatalogDeviceCard({
  equipment,
  catalogDevice,
  meterData,
  wattelemDevice,
  linkedWemVoltage,
  selected,
  onClick,
}: {
  equipment: { id?: number; name: string; type?: string; deviceId?: number | null; deviceSn?: string | null; zoneName?: string };
  catalogDevice?: CatalogDevice;
  meterData?: MeterLatest | null;
  wattelemDevice?: any;
  linkedWemVoltage?: number | null;
  selected: boolean;
  onClick: () => void;
}) {
  const { t, tp } = useI18n();
  const displayName = displayDefaultName(equipment.name, t, tp) || displaySn(catalogDevice?.deviceSn) || displaySn(equipment.deviceSn) || t('eq3_device_number', { id: equipment.deviceId ?? '' });
  // Prefer live MQTT telemetry (wattelemDevice) when equipment is SN-linked
  const tel = wattelemDevice?.current; // MQTT telemetry object { voltage_v, current_a, power_kw, ... }
  const hasData = wattelemDevice ? !!wattelemDevice.online : !!meterData?.timestamp;
  const lastSeen = wattelemDevice?.last_seen || meterData?.timestamp || null;

  // Voltage: WattelEM direct > OCPP rmsVoltage > linked WEM voltage fallback
  const ocppVoltage = meterData?.rmsVoltage != null && Number(meterData.rmsVoltage) > 10 ? Number(meterData.rmsVoltage) : null;
  const voltageV = tel?.voltage_v != null
    ? Number(tel.voltage_v)
    : (ocppVoltage ?? (linkedWemVoltage != null && linkedWemVoltage > 10 ? linkedWemVoltage : null));

  // Power: prefer MQTT, then OCPP activeFPower (use != null to show 0 properly)
  const powerKw = tel?.power_kw != null
    ? Number(tel.power_kw)
    : (meterData?.activeFPower != null ? Number(meterData.activeFPower) / 10_000_000 : null);

  // Current: prefer MQTT, then OCPP rmsCurrent, then compute from P/V
  const ocppCurrent = meterData?.rmsCurrent != null && Number(meterData.rmsCurrent) > 0 ? Number(meterData.rmsCurrent) : null;
  const computedCurrent = powerKw != null && voltageV != null && voltageV > 10 ? (powerKw * 1000) / voltageV : null;
  const currentA = tel?.current_a != null
    ? Number(tel.current_a)
    : (ocppCurrent ?? computedCurrent);
  const modelLower = (catalogDevice?.chargePointModel || equipment.type || '').toLowerCase();
  const deviceImg = modelLower.includes('go')
    ? '/devices/wattelgo.svg'
    : (modelLower.includes('wall') || modelLower.includes('wl'))
      ? '/devices/wattelwall.svg'
      : null;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${
        selected
          ? 'border-zce-blue bg-gradient-to-br from-blue-50 to-white shadow-zce-md'
          : 'border-gray-100 bg-white hover:border-zce-blue/50 hover:shadow-zce'
      }`}
    >
      <div className="flex items-center gap-3 mb-3">
        {deviceImg && (
          <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-sky-100 to-blue-50 border border-sky-200 flex items-center justify-center p-1.5 shadow-sm">
            <img src={deviceImg} alt={modelLower.includes('go') ? 'WattelGo' : 'WattelWall'} className="h-full w-full object-contain" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="font-semibold text-zce-navy text-sm truncate">{displayName}</span>
            <span className={`flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
              hasData ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <span className={`w-2 h-2 rounded-full ${hasData ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
              {hasData ? t('eq_online') : t('eq_offline')}
            </span>
          </div>
          {catalogDevice?.chargePointVendor && (
            <p className="text-xs text-zce-gray-muted truncate">{catalogDevice.chargePointVendor} {catalogDevice.chargePointModel}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs">
        <div className="bg-zce-gray-light rounded-lg p-2 text-center">
          <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">V</span>
          <span className="font-bold text-zce-navy">{voltageV !== null ? voltageV.toFixed(1) : '—'}</span>
        </div>
        <div className="bg-zce-gray-light rounded-lg p-2 text-center">
          <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">A</span>
          <span className="font-bold text-zce-navy">{currentA !== null ? currentA.toFixed(2) : '—'}</span>
        </div>
        <div className="bg-zce-gray-light rounded-lg p-2 text-center">
          <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">kW</span>
          <span className="font-bold text-zce-orange">{powerKw !== null ? powerKw.toFixed(3) : '—'}</span>
        </div>
      </div>
      {equipment.zoneName && (
        <div className="mt-3 text-xs text-zce-gray-muted truncate border-t border-gray-100 pt-2 flex items-center gap-1.5">
          <span>📍</span>
          <span className="truncate">{displayDefaultName(equipment.zoneName, t, tp)}</span>
        </div>
      )}
      <div className="mt-1 text-xs text-zce-gray-muted flex items-center gap-1">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {timeAgo(lastSeen, t)}
      </div>
    </button>
  );
}

function DeviceCard({
  device,
  selected,
  onClick,
}: {
  device: DeviceOverview;
  selected: boolean;
  onClick: () => void;
}) {
  const { t, tp } = useI18n();
  const tel = device.current;
  const snLower = (device.device_sn || '').toLowerCase();
  const deviceImg = snLower.includes('go')
    ? '/devices/wattelgo.svg'
    : (snLower.includes('wall') || snLower.includes('-wl-') || snLower.includes('wl'))
      ? '/devices/wattelwall.svg'
      : null;
  const modelLabel = deviceImg
    ? (deviceImg.includes('wattelgo') ? 'WattelGo' : 'WattelWall')
    : null;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${
        selected
          ? 'border-zce-blue bg-gradient-to-br from-blue-50 to-white shadow-zce-md'
          : 'border-gray-100 bg-white hover:border-zce-blue/50 hover:shadow-zce'
      }`}
    >
      <div className="flex items-center gap-3 mb-3">
        {deviceImg && (
          <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-sky-100 to-blue-50 border border-sky-200 flex items-center justify-center p-1.5 shadow-sm">
            <img src={deviceImg} alt={modelLabel || ''} className="h-full w-full object-contain" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="font-semibold text-zce-navy text-sm truncate">{displaySn(device.device_sn)}</span>
            <span
              className={`flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                device.online
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${device.online ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
              {device.online ? t('eq_online') : t('eq_offline')}
            </span>
          </div>
          {modelLabel && (
            <p className="text-xs text-zce-gray-muted truncate">{modelLabel}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs">
        <div className="bg-zce-gray-light rounded-lg p-2 text-center">
          <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">V</span>
          <span className="font-bold text-zce-navy">{fmt(tel?.voltage_v, 1)}</span>
        </div>
        <div className="bg-zce-gray-light rounded-lg p-2 text-center">
          <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">A</span>
          <span className="font-bold text-zce-navy">{fmt(tel?.current_a, 2)}</span>
        </div>
        <div className="bg-zce-gray-light rounded-lg p-2 text-center">
          <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">kW</span>
          <span className="font-bold text-zce-orange">{fmt(tel?.power_kw, 3)}</span>
        </div>
      </div>
      {device.zones.length > 0 && (
        <div className="mt-3 text-xs text-zce-gray-muted truncate border-t border-gray-100 pt-2">
          📍 {device.zones[0].installation_name} / {displayDefaultName(device.zones[0].zone_name, t, tp)}
        </div>
      )}
      <div className="mt-1 text-xs text-zce-gray-muted flex items-center gap-1">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {timeAgo(device.last_seen, t)}
      </div>
    </button>
  );
}

// Plage de tension plausible sur un réseau 230 V (au-delà : mesure à vérifier).
const VOLTAGE_MIN_V = 180;
const VOLTAGE_MAX_V = 260;

function TelemetryGrid({ device }: { device: DeviceOverview }) {
  const { t, locale } = useI18n();
  const tel = device.current;

  // Calcul émission CO₂ : Énergie (kWh) × facteur d'émission France (0.052 kg CO₂/kWh)
  const CO2_FACTOR_KG_PER_KWH = 0.052; // Mix électrique français
  const energyKwh = tel?.energy_kwh ? Number(tel.energy_kwh) : 0;
  const co2EmissionKg = energyKwh * CO2_FACTOR_KG_PER_KWH;

  // ── Alerte cos φ : colorisation vert / orange / rouge selon les seuils
  //    de facturation du réactif (cf. utils/powerFactor.ts) ──
  const pfValue = tel?.power_factor != null ? Number(tel.power_factor) : null;
  const pfPowerW = tel?.power_kw != null ? Number(tel.power_kw) * 1000 : null;
  const pf = classifyPowerFactor(pfValue, pfPowerW, t);

  // Appareil hors ligne : les valeurs sont la dernière trame reçue, pas une mesure en cours.
  const isOffline = !device.online;
  // Tension hors de la plage plausible d'un réseau 230 V : défaut d'étalonnage ou alimentation inhabituelle.
  const voltage = tel?.voltage_v != null ? Number(tel.voltage_v) : null;
  const voltageAbnormal = voltage != null && voltage > 0 && (voltage < VOLTAGE_MIN_V || voltage > VOLTAGE_MAX_V);

  const items = [
    { label: t('eq_voltage'), value: fmt(tel?.voltage_v, 1), unit: 'V', color: 'text-zce-blue', bg: 'bg-blue-50', icon: '⚡',
      ...(voltageAbnormal ? { badge: t('eq_voltageAbnormal'), badgeClass: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500', hint: t('eq_voltageAbnormalHint', { min: VOLTAGE_MIN_V, max: VOLTAGE_MAX_V }) } : {}) },
    { label: t('eq_current'), value: fmt(tel?.current_a, 3), unit: 'A', color: 'text-zce-orange', bg: 'bg-orange-50', icon: '〰️' },
    { label: t('eq_power'), value: fmt(tel?.power_kw, 3), unit: 'kW', color: 'text-zce-navy', bg: 'bg-indigo-50', icon: '⚡' },
    { label: t('eq_powerFactor'), value: fmt(tel?.power_factor, 3), unit: '', color: pf.text, bg: pf.bg, icon: 'φ', badge: pf.label, badgeClass: pf.badge, dot: pf.dot, hint: pf.hint },
    { label: t('eq_frequency'), value: fmt(tel?.frequency_hz, 1), unit: 'Hz', color: 'text-teal-600', bg: 'bg-teal-50', icon: '∿' },
    { label: t('eq_energy'), value: fmt(tel?.energy_kwh, 3), unit: 'kWh', color: 'text-emerald-600', bg: 'bg-emerald-50', icon: '📊' },
    { label: t('eq_temperature'), value: fmt(tel?.temperature_c, 1), unit: '°C', color: 'text-red-500', bg: 'bg-red-50', icon: '🌡️' },
    { label: t('eq_co2Emission'), value: co2EmissionKg > 0 ? co2EmissionKg.toFixed(3) : '—', unit: 'kg', color: 'text-green-700', bg: 'bg-green-50', icon: '🌿' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-zce-navy flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-zce-navy font-heading">{isOffline ? t('eq_lastReading') : t('eq_realtimeTelemetry')}</h3>
          <p className="text-xs text-zce-gray-muted">{isOffline ? t('eq_lastReadingSub') : t('eq_wattelemInstantReadings')}</p>
        </div>
        {isOffline && (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600" title={device.last_seen ? new Date(device.last_seen).toLocaleString(locale) : undefined}>
            <span className="h-2 w-2 rounded-full bg-gray-400" />
            {t('eq_offlineSince', { ago: timeAgo(device.last_seen, t) })}
          </span>
        )}
      </div>
      <div className={`grid grid-cols-2 md:grid-cols-3 gap-4 ${isOffline ? 'opacity-60 grayscale' : ''}`}>
        {items.map((item) => (
          <div key={item.label} className={`${item.bg} rounded-xl p-4 border border-white`} title={item.hint}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">{item.icon}</span>
              <p className="text-xs text-zce-gray-muted font-medium uppercase tracking-wider">{item.label}</p>
            </div>
            <p className={`text-2xl font-bold ${item.color} font-heading`}>
              {item.value}
              {item.unit && <span className="text-sm font-normal text-zce-gray-muted ml-1">{item.unit}</span>}
            </p>
            {item.badge && (
              <span className={`inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${item.badgeClass}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${item.dot}`} />
                {item.badge}
              </span>
            )}
          </div>
        ))}
      </div>
      {tel?.recorded_at && (
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-end gap-2 text-xs text-zce-gray-muted">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {t('eq_recordedAt', { date: new Date(tel.recorded_at).toLocaleString(locale) })}
        </div>
      )}
    </div>
  );
}

function RelayControl({
  device,
  onRelay,
  pending,
}: {
  device: DeviceOverview;
  onRelay: (value: 0 | 1) => void;
  pending: boolean;
}) {
  const { t } = useI18n();
  const isOn = device.relay_state === 1;
  const isOffline = !device.online;
  return (
    <div className={`bg-white rounded-xl shadow-zce border border-gray-100 p-6 ${isOffline ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-zce-orange flex items-center justify-center">
          <svg className="w-5 h-5 text-zce-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-zce-navy font-heading">{t('eq_relayControl')}</h3>
          <p className="text-xs text-zce-gray-muted">{t('eq_powerSupplyManagement')}</p>
        </div>
      </div>

      {/* Offline banner */}
      {isOffline && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <svg className="w-4 h-4 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-amber-700 font-medium">{t('eq_offlineCommandUnavailable')}</p>
        </div>
      )}

      {/* État + boutons */}
      <div className="flex items-center justify-between bg-zce-gray-light rounded-xl p-4">
        <div>
          <p className="text-xs text-zce-gray-muted font-medium uppercase tracking-wider mb-1">{t('eq_currentState')}</p>
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${isOffline ? 'bg-gray-400' : isOn ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
            <p className={`text-xl font-bold font-heading ${isOffline ? 'text-zce-gray-muted' : isOn ? 'text-emerald-600' : 'text-zce-gray-muted'}`}>
              {isOffline ? t('eq_stateOffline') : isOn ? t('eq_stateActive') : device.relay_state === 0 ? t('eq_stateInactive') : t('eq_stateUnknown')}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => onRelay(1)}
            disabled={isOffline || pending || isOn}
            title={isOffline ? t('eq_deviceOffline') : t('eq_relayOnHint')}
            className="px-5 py-2.5 text-sm font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
          >
            {pending ? '...' : `✓ ${t('eq2_state_on')}`}
          </button>
          <button
            onClick={() => onRelay(0)}
            disabled={isOffline || pending || device.relay_state === 0}
            title={isOffline ? t('eq_deviceOffline') : t('eq_relayOffHint')}
            className="px-5 py-2.5 text-sm font-semibold bg-rose-500 text-white rounded-lg hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
          >
            {pending ? '...' : `✕ ${t('eq2_state_off')}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Mode de pilotage d'un module (réservé aux administrateurs) ──
const CONTROL_MODE_OPTIONS: Array<{ value: ControlMode; icon: string; title: TranslationKey; desc: TranslationKey }> = [
  {
    value: 'controllable',
    icon: '⚡',
    title: 'eq_modeControllable',
    desc: 'eq_modeControllableDesc',
  },
  {
    value: 'meter_only',
    icon: '🔒',
    title: 'eq_modeMeterOnly',
    desc: 'eq_modeMeterOnlyDesc',
  },
];

function ControlModeCard({
  mode,
  saving,
  error,
  onChange,
}: {
  mode: ControlMode;
  saving: boolean;
  error: string | null;
  onChange: (mode: ControlMode) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-zce-navy flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-zce-navy font-heading">{t('eq_controlMode')}</h3>
          <p className="text-xs text-zce-gray-muted">{t('eq_adminOnlySetting')}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label={t('eq_controlMode')}>
        {CONTROL_MODE_OPTIONS.map((o) => {
          const active = mode === o.value;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={saving || active}
              onClick={() => onChange(o.value)}
              className={`text-left p-4 rounded-xl border-2 transition-all disabled:cursor-default ${
                active ? 'border-zce-navy bg-zce-gray-light' : 'border-gray-200 bg-white hover:border-gray-300'
              } ${saving && !active ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-semibold text-zce-navy">{o.icon} {t(o.title)}</span>
                {active && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">{t('eq_active')}</span>
                )}
              </div>
              <p className="text-xs text-zce-gray-muted">{t(o.desc)}</p>
            </button>
          );
        })}
      </div>
      {error && (
        <p className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2">{error}</p>
      )}
    </div>
  );
}

// ── Affiché à la place du contrôle relay pour un non-admin sur un module « compteur seul » ──
function MeterOnlyNotice() {
  const { t } = useI18n();
  return (
    <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-lg" aria-hidden>🔒</div>
        <div>
          <h3 className="text-lg font-semibold text-zce-navy font-heading">{t('eq_modeMeterOnly')}</h3>
          <p className="text-xs text-zce-gray-muted">
            {t('eq_meterOnlyNotice')}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Contrôle Relay pour WattelGo/WattelWall (OCPP: start/stop transaction) ──
function CatalogRelayControl({ deviceSn, isConnected: initialConnected }: { deviceSn: string; isConnected: boolean }) {
  const { t } = useI18n();
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState<'idle' | 'charging' | 'unknown'>('unknown');
  const [isConnected, setIsConnected] = useState(initialConnected);
  const [lastTransactionId, setLastTransactionId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch real-time status from device-service on mount
  useEffect(() => {
    // 1) Restauration immédiate de l'état "en charge" depuis localStorage
    //    => survit au rafraîchissement de la page / à la navigation.
    const persisted = getBorneSession(deviceSn);
    if (persisted) {
      setStatus('charging');
      setIsConnected(true);
      if (persisted.transactionId) setLastTransactionId(persisted.transactionId);
    }

    const DEVICE_SERVICE_URL = import.meta.env.VITE_DEVICE_SERVICE_URL || '';
    if (!DEVICE_SERVICE_URL || !deviceSn) return;
    const token = localStorage.getItem('dashboard_zce_token');
    fetch(`${DEVICE_SERVICE_URL}/api/v1/devices`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
      .then(r => r.json())
      .then(res => {
        if (!res.success || !res.data) return;
        const dev = res.data.find((d: any) => d.device_sn === deviceSn);
        if (dev) {
          // Statut temps réel OCPP (peut être absent) + statut device (ex: 'active')
          const rtStatus = String(dev.realtimeStatus?.status || '').toLowerCase();
          const devStatus = String(dev.device_status || '').toLowerCase();
          const CONNECTED = ['connected', 'available', 'preparing', 'charging'];
          // La borne est considérée en ligne si le statut temps réel l'indique
          // OU si son device_status vaut 'active'/'available'/'online' (cas sans realtimeStatus).
          const connected = CONNECTED.includes(rtStatus)
            || ['active', 'available', 'online', 'connected', 'preparing', 'charging'].includes(devStatus);
          setIsConnected(connected);
          // Ne PAS écraser un état "en charge" restauré depuis localStorage.
          const hasPersisted = !!getBorneSession(deviceSn);
          if (rtStatus === 'charging') {
            setStatus('charging');
          } else if (connected && !hasPersisted) {
            setStatus('idle');
          }
          // Check active transactions
          if (dev.statistics?.activeTransactions > 0 || dev.charging_transactions?.length > 0) {
            setStatus('charging');
            const activeTx = dev.charging_transactions?.[0];
            if (activeTx?.transaction_id) setLastTransactionId(activeTx.transaction_id);
          }
        }
      })
      .catch(() => {});
  }, [deviceSn]);

  // Resynchronise l'état du bouton avec la session persistée : start/stop déclenché
  // ailleurs, synchro inter-onglets, et surtout nettoyage anti-fantôme du poller
  // OCPP parent (fin de charge réelle) → le bouton repasse à "PRÊT".
  useEffect(() => {
    const sync = () => {
      const s = getBorneSession(deviceSn);
      if (s) {
        setStatus('charging');
        if (s.transactionId) setLastTransactionId(s.transactionId);
      } else {
        setStatus(prev => (prev === 'charging' ? 'idle' : prev));
      }
    };
    window.addEventListener(BORNE_SESSION_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(BORNE_SESSION_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [deviceSn]);

  const handleStart = async () => {
    setPending(true);
    setError(null);
    // Optimiste : on persiste la session et on passe "en charge" immédiatement
    // (l'état survivra au refresh même si l'appel réseau est lent).
    const startedAt = Date.now();
    const optimisticTxId = Math.floor(startedAt / 1000);
    setStatus('charging');
    startBorneSession({
      deviceSn,
      transactionId: optimisticTxId,
      chargeStartedAt: startedAt,
      startEnergyWh: null,
      lastEnergyWh: null,
      sessionEnergyWh: 0,
      lastTickAt: startedAt,
      fallbackPowerW: 0,
    });
    try {
      const result = await startTransaction(deviceSn, 1, 'DASHBOARD');
      if (result.success) {
        setStatus('charging');
        const txId = result.data?.transactionId;
        if (txId) {
          setLastTransactionId(txId);
          startBorneSession({
            deviceSn,
            transactionId: txId,
            chargeStartedAt: startedAt,
            startEnergyWh: null,
            lastEnergyWh: null,
            sessionEnergyWh: 0,
            lastTickAt: startedAt,
            fallbackPowerW: 0,
          });
        } else {
          setLastTransactionId(optimisticTxId);
        }
      } else {
        // Échec réel côté borne => on annule l'état optimiste
        endBorneSession(deviceSn);
        setStatus('idle');
        setError(result.error || t('eq_startFailed'));
      }
    } catch (e) {
      endBorneSession(deviceSn);
      setStatus('idle');
      setError((e as Error).message);
    } finally {
      setPending(false);
    }
  };

  const handleStop = async () => {
    setPending(true);
    setError(null);
    try {
      let txId = lastTransactionId;
      // If no transactionId known, try to fetch it from device-service
      if (!txId) {
        const DEVICE_SERVICE_URL = import.meta.env.VITE_DEVICE_SERVICE_URL || '';
        const token = localStorage.getItem('dashboard_zce_token');
        const res = await fetch(`${DEVICE_SERVICE_URL}/api/v1/status/chargers/${encodeURIComponent(deviceSn)}/transactions/active`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const data = await res.json();
        if (data.success && data.data?.transactionId) {
          txId = data.data.transactionId;
        } else if (data.data?.transaction_id) {
          txId = data.data.transaction_id;
        }
      }
      if (!txId) {
        // Pas de transaction réelle connue : on coupe quand même l'état local
        // (cas d'une session optimiste de test) pour ne pas rester bloqué "EN CHARGE".
        endBorneSession(deviceSn);
        setStatus('idle');
        setLastTransactionId(null);
        setPending(false);
        return;
      }
      const result = await stopTransaction(deviceSn, txId);
      if (result.success) {
        endBorneSession(deviceSn);
        setStatus('idle');
        setLastTransactionId(null);
      } else {
        setError(result.error || t('eq_stopFailed'));
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPending(false);
    }
  };

  const isOn = status === 'charging';

  return (
    <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-zce-orange flex items-center justify-center">
          <svg className="w-5 h-5 text-zce-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-zce-navy font-heading">{t('eq_relayControl')}</h3>
          <p className="text-xs text-zce-gray-muted">{t('eq_powerSupplyManagementOcpp')}</p>
        </div>
      </div>

      <div className="flex items-center justify-between bg-zce-gray-light rounded-xl p-4">
        <div>
          <p className="text-xs text-zce-gray-muted font-medium uppercase tracking-wider mb-1">{t('eq_currentState')}</p>
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${isOn ? 'bg-emerald-500 animate-pulse' : isConnected ? 'bg-amber-400' : 'bg-gray-400'}`} />
            <p className={`text-xl font-bold font-heading ${isOn ? 'text-emerald-600' : 'text-zce-gray-muted'}`}>
              {isOn ? t('eq_stateCharging') : isConnected ? t('eq_stateReady') : t('eq_stateOffline')}
            </p>
          </div>
          {lastTransactionId && (
            <p className="text-xs text-zce-gray-muted mt-1">{t('eq3_transaction_number', { id: lastTransactionId })}</p>
          )}
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleStart}
            disabled={pending || isOn || !isConnected}
            className="px-5 py-2.5 text-sm font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
          >
            {pending ? '...' : `✓ ${t('eq2_state_on')}`}
          </button>
          <button
            onClick={handleStop}
            disabled={pending || !isOn}
            className="px-5 py-2.5 text-sm font-semibold bg-rose-500 text-white rounded-lg hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
          >
            {pending ? '...' : `✕ ${t('eq2_state_off')}`}
          </button>
        </div>
      </div>

      {error && (
        <p className="text-red-600 text-xs mt-3 bg-red-50 p-2 rounded-lg">{error}</p>
      )}
    </div>
  );
}

function ZoneInfo({ device }: { device: DeviceOverview }) {
  const { t, tp } = useI18n();
  if (device.zones.length === 0) return null;
  
  // Énergie totale du WattelEM (en Wh pour cohérence avec meter_energy_wh)
  const wattelemEnergyWh = device.current?.energy_kwh ? Number(device.current.energy_kwh) * 1000 : 0;
  const wattelemPowerW = device.current?.power_kw ? Number(device.current.power_kw) * 1000 : 0;
  
  return (
    <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-zce-blue flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-zce-navy font-heading">{t('eq_linkedZones')}</h3>
          <p className="text-xs text-zce-gray-muted">{tp('eq_zonesConnected', device.zones.length)}</p>
        </div>
      </div>
      <div className="space-y-4">
        {device.zones.map((z) => {
          // Calculer la somme des énergies/puissances mesurées pour cette zone
          const measuredEnergyWh = z.equipment
            .filter(eq => eq.meter_energy_wh !== null)
            .reduce((sum, eq) => sum + (eq.meter_energy_wh || 0), 0);
          const measuredPowerW = z.equipment
            .filter(eq => eq.meter_power_w !== null)
            .reduce((sum, eq) => sum + (eq.meter_power_w || 0), 0);
          
          // Énergie/puissance déduite = WattelEM - somme mesurées
          const deducedEnergyWh = Math.max(0, wattelemEnergyWh - measuredEnergyWh);
          const deducedPowerW = Math.max(0, wattelemPowerW - measuredPowerW);
          
          // Compter les équipements non mesurés (type null = prise)
          const unmeasuredEquipments = z.equipment.filter(eq => eq.type === null && eq.meter_power_w === null);
          
          return (
          <div key={z.zone_id} className="border border-gray-100 rounded-xl p-4 hover:border-zce-blue/30 transition-colors">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">{z.zone_icon}</span>
              <span className="font-semibold text-zce-navy">{displayDefaultName(z.zone_name, t, tp)}</span>
            </div>
            <p className="text-xs text-zce-gray-muted mb-3 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('eq3_installation_colon')} <span className="font-medium text-zce-navy">{z.installation_name}</span>
            </p>
            {z.equipment.length > 0 && (
              <div className="space-y-2">
                {z.equipment.map((eq) => {
                  // Équipement non mesuré (type null = prise) : afficher énergie déduite
                  const isUnmeasured = eq.type === null && eq.meter_power_w === null;
                  // Répartir l'énergie déduite entre les équipements non mesurés
                  const eqDeducedEnergyWh = isUnmeasured && unmeasuredEquipments.length > 0 
                    ? deducedEnergyWh / unmeasuredEquipments.length 
                    : 0;
                  const eqDeducedPowerW = isUnmeasured && unmeasuredEquipments.length > 0 
                    ? deducedPowerW / unmeasuredEquipments.length 
                    : 0;
                  
                  return (
                  <div key={eq.id} className="bg-zce-gray-light rounded-lg p-3 flex items-center justify-between hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{eq.icon}</span>
                      <span className="text-sm font-medium text-zce-gray-text">{displayDefaultName(eq.name, t, tp)}</span>
                    </div>
                    {eq.meter_power_w !== null ? (
                      <div className="text-right">
                        <div className="text-sm font-bold text-emerald-600 flex items-center gap-1">
                          ⚡ {(eq.meter_power_w / 10_000_000).toFixed(3)} <span className="text-xs font-normal text-zce-gray-muted">kW</span>
                        </div>
                        <div className="text-xs text-zce-gray-muted">
                          {(eq.meter_energy_wh! / 10_000_000).toFixed(3)} kWh
                        </div>
                      </div>
                    ) : isUnmeasured && wattelemEnergyWh > 0 ? (
                      <div className="text-right">
                        <div className="text-sm font-bold text-zce-orange flex items-center gap-1">
                          ⚡ {(eqDeducedPowerW / 1000).toFixed(2)} <span className="text-xs font-normal text-zce-gray-muted">kW</span>
                          <span className="ml-1 text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-semibold">{t('eq_estimated')}</span>
                        </div>
                        <div className="text-xs text-orange-400">
                          {(eqDeducedEnergyWh / 1000).toFixed(1)} kWh
                        </div>
                      </div>
                    ) : (
                      eq.power_w ? (
                        <span className="text-xs text-zce-gray-muted bg-gray-200 px-2 py-1 rounded-full">{t('eq_nominal', { n: eq.power_w })}</span>
                      ) : null
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// Page
// ============================================

function WattelEMPage() {
  const { t, tp, locale } = useI18n();
  const [searchParams] = useSearchParams();
  const snFromUrl = searchParams.get('sn'); // SN pré-sélectionné depuis l'URL (ex: depuis Architecture - tableau)
  const deviceIdFromUrl = searchParams.get('deviceId'); // deviceId depuis l'URL (ex: depuis Architecture - point avec device)
  
  // Paramètres pour les points sans device (données calculées)
  const pointNameFromUrl = searchParams.get('pointName');
  const pointTypeFromUrl = searchParams.get('pointType');
  const pointDeducedEnergyWhFromUrl = searchParams.get('pointDeducedEnergyWh'); // Wh déduits depuis WattelEM
  const pointDeducedPowerWFromUrl = searchParams.get('pointDeducedPowerW'); // W déduits depuis WattelEM
  const pointEqIdFromUrl = searchParams.get('pointEqId'); // ID équipement pour recalcul live
  const isSimplePointMode = !!pointNameFromUrl;
  
  const [catalogDevices, setCatalogDevices] = useState<CatalogDevice[]>([]);

  // Borne OCPP (Wattel 22, WattelGo, WattelWall...) résolue depuis le device-service
  // quand le deviceId de l'URL n'est PAS dans le catalogue MQTT (catalog-devices).
  // Permet d'afficher les vraies données (mode catalogue) pour une borne du schéma.
  const [borneInfo, setBorneInfo] = useState<CatalogDevice | null>(null);
  // Statut de session OCPP temps réel (puissance/énergie réelles) — même source que la page Bornes.
  const [borneSession, setBorneSession] = useState<ChargingSessionStatus | null>(null);
  // Ticker 1s : force le recalcul de l'énergie/puissance live pendant la charge
  // (effet "temps réel" même entre deux polls OCPP de 5s).
  const [liveTick, setLiveTick] = useState(0);

  // ── Filtres Installation / Device ──
  const [installations, setInstallations] = useState<InstallationData[]>([]);
  const [installationsLoaded, setInstallationsLoaded] = useState(false);
  const [selectedInstallationId, setSelectedInstallationId] = useState<number | null>(null);
  const [filterDeviceSn, setFilterDeviceSn] = useState<string | null>(null);

  const {
    devices,
    selectedDevice,
    selectedSn,
    dailyEnergy,
    loading,
    error,
    lastUpdate,
    relayPending,
    loadOverview,
    selectDevice,
    sendRelay,
  } = useWattelEM();

  // État pour les données du device catalogue (meter values)
  const [catalogMeterData, setCatalogMeterData] = useState<MeterLatest | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogDailyEnergy, setCatalogDailyEnergy] = useState<{ day: string; daily_kwh: number; avg_power_kw: number; max_power_kw: number }[]>([]);
  const [catalogEnergyLoading, setCatalogEnergyLoading] = useState(false);
  // Énergie de la session de recharge en cours / dernière (transaction OCPP)
  // = (dernière activeFEnergy dans meter_values) - (meter_start de la transaction)
  const [catalogSessionEnergy, setCatalogSessionEnergy] = useState<SessionEnergy | null>(null);

  // ── Planification (V1 — localStorage) ──
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const {
    schedule: deviceSchedule,
    setSchedule: setDeviceSchedule,
    reload: reloadDeviceSchedule,
  } = useDeviceSchedule(selectedDevice?.device_sn ?? null);

  // ── Exécuteur de planning : envoie automatiquement les commandes relai ──
  // Tourne toutes les 30 s sur tous les devices ayant un planning actif.
  // forceManual(sn, value) : un clic ON/OFF manuel a été fait
  //   → la planification est temporairement gelée pour ce device
  //   → reprise AUTOMATIQUE au prochain événement de la planification
  //     (ex: planning dit "ON 24h, prochain OFF à 16h44" → clic OFF maintenant
  //          → reste OFF, planning reprend à 16h44 sans intervention)
  const { forceManual } = useScheduleExecutor(
    devices,
    sendRelay,
    { intervalMs: 30_000, cooldownMs: 90_000 },
  );

  // ── Droits de pilotage + mode « compteur seul » ──
  // Le serveur fait foi (il refuse de toute façon ce qui n'est pas permis).
  // Le niveau calculé localement ne sert qu'à afficher les bons contrôles
  // immédiatement, avant sa réponse, ou si elle échoue.
  const { user, hasOrgRole } = useAuth();
  const localControlLevel: ControlLevel =
    user?.platformRole === 'super_admin' || hasOrgRole('org_admin')
      ? 'admin'
      : hasOrgRole('maintenance') ? 'operator' : 'none';
  const [serverControlLevel, setServerControlLevel] = useState<ControlLevel | null>(null);
  const [meterOnlySns, setMeterOnlySns] = useState<Set<string>>(() => new Set());
  const [controlModeSaving, setControlModeSaving] = useState(false);
  const [controlModeError, setControlModeError] = useState<string | null>(null);

  useEffect(() => {
    wattelEMApi.getControlSettings()
      .then((s) => {
        setServerControlLevel(s.level);
        setMeterOnlySns(new Set(s.meterOnly));
      })
      .catch(() => { /* niveau local en secours */ });
  }, []);

  useEffect(() => { setControlModeError(null); }, [selectedSn]);

  const controlLevel = serverControlLevel ?? localControlLevel;
  const selectedIsMeterOnly = !!selectedDevice && meterOnlySns.has(selectedDevice.device_sn);
  const canControlSelected = controlLevel === 'admin' || (controlLevel === 'operator' && !selectedIsMeterOnly);
  const scheduleReadOnlyReason = canControlSelected
    ? null
    : selectedIsMeterOnly
      ? t('eq_scheduleReadOnlyMeterOnly')
      : t('eq_scheduleReadOnlyRole');

  const changeControlMode = async (sn: string, mode: ControlMode) => {
    if (
      mode === 'meter_only'
      && deviceSchedule?.enabled
      && !window.confirm(t('eq_confirmMeterOnly'))
    ) {
      return;
    }
    setControlModeSaving(true);
    setControlModeError(null);
    try {
      const res = await wattelEMApi.setControlMode(sn, mode);
      setMeterOnlySns((prev) => {
        const next = new Set(prev);
        if (res.mode === 'meter_only') next.add(sn);
        else next.delete(sn);
        return next;
      });
      // Le serveur a désactivé le planning dans la même transaction : on relit
      // pour que la carte Planification l'affiche aussitôt.
      if (res.scheduleDisabled) reloadDeviceSchedule();
    } catch (err) {
      setControlModeError((err as Error).message);
    } finally {
      setControlModeSaving(false);
    }
  };

  // ── Time range filter for charts ──
  const DEFAULT_TIME_FILTER: TimeFilterValue = { range: '30d', days: 30 };
  const [chartTimeFilter, setChartTimeFilter] = useState<TimeFilterValue>(DEFAULT_TIME_FILTER);
  const [filteredDailyEnergy, setFilteredDailyEnergy] = useState<typeof dailyEnergy>([]);
  const [filteredEnergyLoading, setFilteredEnergyLoading] = useState(false);

  // Fetch daily energy when time filter changes (for WattelEM devices)
  useEffect(() => {
    if (!selectedSn) { setFilteredDailyEnergy([]); return; }
    setFilteredEnergyLoading(true);
    const fetchData = chartTimeFilter.range === '24h'
      ? wattelEMApi.getHourlyEnergy(selectedSn, 24)
      : wattelEMApi.getDailyEnergy(selectedSn, chartTimeFilter.days, chartTimeFilter.startDate, chartTimeFilter.endDate);
    fetchData
      .then(data => setFilteredDailyEnergy(data))
      .catch(err => { console.error('[WattelEM] Erreur chargement énergie filtrée:', err); setFilteredDailyEnergy([]); })
      .finally(() => setFilteredEnergyLoading(false));
  }, [selectedSn, chartTimeFilter]);

  // Catalog time filter state
  const [catalogTimeFilter, setCatalogTimeFilter] = useState<TimeFilterValue>(DEFAULT_TIME_FILTER);
  const [filteredCatalogEnergy, setFilteredCatalogEnergy] = useState<typeof catalogDailyEnergy>([]);
  const [filteredCatalogLoading, setFilteredCatalogLoading] = useState(false);

  // Fetch catalog energy when time filter changes
  useEffect(() => {
    if (!deviceIdFromUrl) { setFilteredCatalogEnergy([]); return; }
    const deviceId = parseInt(deviceIdFromUrl, 10);
    if (isNaN(deviceId)) return;
    setFilteredCatalogLoading(true);
    const fetchData = catalogTimeFilter.range === '24h'
      ? architectureApi.getMeterValuesHourly(deviceId, 24).then(res => res.data || [])
      : architectureApi.getMeterValuesHistory(deviceId, catalogTimeFilter.days, catalogTimeFilter.startDate, catalogTimeFilter.endDate).then(res => res.data || []);
    fetchData
      .then(data => setFilteredCatalogEnergy(data))
      .catch(err => { console.error('[WattelEM] Erreur chargement historique catalogue filtré:', err); setFilteredCatalogEnergy([]); })
      .finally(() => setFilteredCatalogLoading(false));
  }, [deviceIdFromUrl, catalogTimeFilter]);

  // Charger les installations pour les filtres + catalog devices
  useEffect(() => {
    architectureApi.getInstallations()
      .then(res => { setInstallations(res.data || []); setInstallationsLoaded(true); })
      .catch(err => console.error('[WattelEM] Erreur chargement installations:', err));
    architectureApi.getCatalogDevices()
      .then(res => setCatalogDevices(prev => prev.length > 0 ? prev : (res.data || [])))
      .catch(err => console.error('[WattelEM] Erreur chargement catalog:', err));
    // Toujours charger les devices WattelEM (telemetrie live) pour le calcul de puissance déduite
    loadOverview();
  }, []);

  // Auto-detect installation from URL params or installationId query param
  const installationIdFromUrl = searchParams.get('installationId');
  useEffect(() => {
    if (selectedInstallationId) return; // already set by user
    if (installations.length === 0) return;
    // If installationId explicitly in URL
    if (installationIdFromUrl) {
      setSelectedInstallationId(Number(installationIdFromUrl));
      return;
    }
    // Auto-detect from SN: find which installation has a zone with this SN
    if (snFromUrl) {
      const found = installations.find(inst =>
        inst.zones.some(z => (z.wattelemSn || '').split(',').map(s => s.trim().toLowerCase()).includes(snFromUrl.toLowerCase()))
      );
      if (found) {
        setSelectedInstallationId(found.id);
        setFilterDeviceSn(snFromUrl);
      }
    }
    // Auto-detect from deviceId: find which installation has equipment with this deviceId
    if (deviceIdFromUrl) {
      const did = parseInt(deviceIdFromUrl, 10);
      const found = installations.find(inst =>
        inst.zones.some(z => (z.equipment || []).some(eq => eq.deviceId === did))
      );
      if (found) {
        setSelectedInstallationId(found.id);
        setFilterDeviceSn(`catalog:${did}`);
      }
    }
  }, [installations, snFromUrl, deviceIdFromUrl, installationIdFromUrl]);

  // SNs des devices WattelEM liés à l'installation sélectionnée.
  // Aucune installation sélectionnée → on ne montre AUCUN device (évite d'afficher
  // des capteurs avant que l'utilisateur ait choisi une installation).
  const installationDeviceSns = (() => {
    if (!selectedInstallationId) return null;
    const sourceInstallations = installations.filter(i => i.id === selectedInstallationId);
    if (sourceInstallations.length === 0) return null;
    const sns = sourceInstallations
      .flatMap(inst => inst.zones)
      .flatMap(z => (z.wattelemSn || '').split(',').map(s => s.trim()).filter(Boolean))
      .map(sn => sn.toLowerCase());
    const unique = [...new Set(sns)];
    return unique.length > 0 ? unique : null;
  })();

  // Equipment from the selected installation that have a deviceId (WattelGo, WattelWall, etc.)
  const installationCatalogEquipment = (() => {
    if (!selectedInstallationId) return [];
    const inst = installations.find(i => i.id === selectedInstallationId);
    if (!inst) return [];
    return inst.zones
      .flatMap(z => (z.equipment || []).map(eq => ({ ...eq, zoneName: z.name })))
      // Include both legacy deviceId-linked equipment AND new SN-linked equipment
      .filter(eq => (eq.deviceId != null && eq.deviceId > 0) || !!eq.deviceSn);
  })();

  // Meter values for all catalog devices in the selected installation
  const [installationMeterLatest, setInstallationMeterLatest] = useState<Record<number, MeterLatest>>({});
  useEffect(() => {
    // En mode point simple, charger les meter values de l'installation contenant le point
    let ids: number[] = [];
    if (isSimplePointMode && pointNameFromUrl && installations.length > 0) {
      for (const inst of installations) {
        const zone = inst.zones.find(z => (z.equipment || []).some(eq => eq.name === pointNameFromUrl));
        if (zone) {
          ids = (zone.equipment || [])
            .map(eq => eq.deviceId)
            .filter((id): id is number => id != null && id > 0);
          break;
        }
      }
    } else {
      ids = installationCatalogEquipment.map(eq => eq.deviceId).filter((id): id is number => id != null && id > 0);
    }
    if (ids.length === 0) return;
    architectureApi.getLatestMeterValues(ids)
      .then(res => {
        const map: Record<number, MeterLatest> = {};
        (res.data || []).forEach(m => { map[m.deviceId] = m; });
        setInstallationMeterLatest(map);
      })
      .catch(err => console.error('[WattelEM] Erreur chargement meter values installation:', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstallationId, installationCatalogEquipment.length, isSimplePointMode, pointNameFromUrl, installations.length]);

  // Helper: compute deduced energy (Wh) and power (W) for an unmeasured equipment (like Prise 16A)
  // Same logic as the schema: WattelEM - sum of measured catalog devices, divided by # unmeasured
  const computeDeducedForEquipment = (equipmentId: number): { energyWh: number; powerW: number } => {
    if (!selectedInstallationId) return { energyWh: 0, powerW: 0 };
    const inst = installations.find(i => i.id === selectedInstallationId);
    if (!inst) return { energyWh: 0, powerW: 0 };
    const zone = inst.zones.find(z => (z.equipment || []).some(e => e.id === equipmentId));
    if (!zone) return { energyWh: 0, powerW: 0 };
    const zoneSns = (zone.wattelemSn || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    let wattelemEnergyWh = 0;
    let wattelemPowerW = 0;
    zoneSns.forEach(sn => {
      const dev = devices.find(d => d.device_sn.toLowerCase() === sn);
      if (dev?.current?.energy_kwh) wattelemEnergyWh += Number(dev.current.energy_kwh) * 1000;
      if (dev?.current?.power_kw) wattelemPowerW += Number(dev.current.power_kw) * 1000;
    });
    let measuredEnergyWh = 0;
    let measuredPowerW = 0;
    (zone.equipment || []).forEach(eq => {
      if (eq.deviceId && eq.deviceId > 0) {
        const m = installationMeterLatest[eq.deviceId];
        if (m?.activeFEnergy != null) measuredEnergyWh += Number(m.activeFEnergy) / 10_000;
        if (m?.activeFPower != null) measuredPowerW += Number(m.activeFPower) / 10_000;
      }
    });
    const deducedEnergyWh = Math.max(0, wattelemEnergyWh - measuredEnergyWh);
    const deducedPowerW = Math.max(0, wattelemPowerW - measuredPowerW);
    const unmeasured = (zone.equipment || []).filter(eq => !eq.deviceId || eq.deviceId <= 0);
    if (unmeasured.length === 0) return { energyWh: 0, powerW: 0 };
    return {
      energyWh: deducedEnergyWh / unmeasured.length,
      powerW: deducedPowerW / unmeasured.length,
    };
  };

  const navigateToPoint = (equipment: { id?: number; name: string; power?: number | null; type?: string | null }) => {
    const { energyWh, powerW } = equipment.id != null ? computeDeducedForEquipment(equipment.id) : { energyWh: 0, powerW: 0 };
    const instParam = selectedInstallationId ? `&installationId=${selectedInstallationId}` : '';
    const params = new URLSearchParams({
      pointName: equipment.name,
      pointPower: String(equipment.power || 0),
      pointType: equipment.type || 'prise',
      pointDeducedEnergyWh: energyWh.toFixed(4),
      pointDeducedPowerW: powerW.toFixed(4),
    });
    if (equipment.id != null) params.set('pointEqId', String(equipment.id));
    window.location.href = `/wattel-em?${params.toString()}${instParam}`;
  };

  // Compute live deduced values when in simple point mode (so data is fresh even after async loads)
  const liveDeduced = isSimplePointMode && pointEqIdFromUrl
    ? computeDeducedForEquipment(Number(pointEqIdFromUrl))
    : null;
  const displayedDeducedEnergyWh = liveDeduced && liveDeduced.energyWh > 0
    ? liveDeduced.energyWh
    : (pointDeducedEnergyWhFromUrl ? Number(pointDeducedEnergyWhFromUrl) : 0);
  const displayedDeducedPowerW = liveDeduced && liveDeduced.powerW > 0
    ? liveDeduced.powerW
    : (pointDeducedPowerWFromUrl ? Number(pointDeducedPowerWFromUrl) : 0);

  // Charger les catalog devices pour mapper deviceId → deviceSn
  // Load when deviceIdFromUrl is set OR when an installation is selected
  useEffect(() => {
    if (deviceIdFromUrl || selectedInstallationId) {
      setCatalogLoading(true);
      architectureApi.getCatalogDevices()
        .then(res => {
          setCatalogDevices(res.data || []);
        })
        .catch(err => console.error('[WattelEM] Erreur chargement catalog:', err));
      
      // Charger aussi les meter values pour ce deviceId
      const deviceId = parseInt(deviceIdFromUrl ?? '', 10);
      if (!isNaN(deviceId)) {
        architectureApi.getLatestMeterValues([deviceId])
          .then(res => {
            console.log('[WattelEM] Meter values pour deviceId', deviceId, ':', res.data);
            if (res.data && res.data.length > 0) {
              const incoming = res.data[0];
              setCatalogMeterData(prev => ({
                ...incoming,
                activeFEnergy: incoming.activeFEnergy ?? prev?.activeFEnergy ?? null,
                activeFPower: incoming.activeFPower ?? prev?.activeFPower ?? null,
                activeFPowerTs: incoming.activeFPower != null ? incoming.activeFPowerTs : (prev?.activeFPowerTs ?? null),
                rmsVoltage: incoming.rmsVoltage ?? prev?.rmsVoltage ?? null,
                rmsCurrent: incoming.rmsCurrent ?? prev?.rmsCurrent ?? null,
                rmsCurrentTs: incoming.rmsCurrent != null ? incoming.rmsCurrentTs : (prev?.rmsCurrentTs ?? null),
                temperature: incoming.temperature ?? prev?.temperature ?? null,
              }));
            }
          })
          .catch(err => console.error('[WattelEM] Erreur chargement meter values:', err))
          .finally(() => setCatalogLoading(false));

        // Charger l'historique de consommation
        setCatalogEnergyLoading(true);
        architectureApi.getMeterValuesHistory(deviceId, 30)
          .then(res => {
            console.log('[WattelEM] Historique énergie pour deviceId', deviceId, ':', res.data?.length, 'jours');
            setCatalogDailyEnergy(res.data || []);
          })
          .catch(err => console.error('[WattelEM] Erreur chargement historique énergie:', err))
          .finally(() => setCatalogEnergyLoading(false));
      } else {
        setCatalogLoading(false);
      }
    }
  }, [deviceIdFromUrl, selectedInstallationId]);

  // 5s polling for catalog device meter values (real-time telemetry refresh)
  // + 30s polling for history (energy chart). Tracks last refresh time for UI badge.
  const [lastMeterRefresh, setLastMeterRefresh] = useState<Date | null>(null);
  const [nowTick, setNowTick] = useState(Date.now()); // ticks every 1s for countdown
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const deviceId = parseInt(deviceIdFromUrl ?? '', 10);
    if (isNaN(deviceId)) return;

    const refreshLatest = () => {
      architectureApi.getLatestMeterValues([deviceId])
        .then(res => {
          // ALWAYS update the timestamp so the user sees the heartbeat,
          // even if the OCPP borne hasn't pushed new MeterValues yet.
          setLastMeterRefresh(new Date());
          if (res.data && res.data.length > 0) {
            const incoming = res.data[0];
            // MERGE: keep the last known non-null value per field. An OCPP borne
            // pushes one measurand at a time (temp now, power in 30s...), so a poll
            // returning a partial row must NOT wipe out previously received values.
            setCatalogMeterData(prev => ({
              ...incoming,
              activeFEnergy: incoming.activeFEnergy ?? prev?.activeFEnergy ?? null,
              activeFPower: incoming.activeFPower ?? prev?.activeFPower ?? null,
              activeFPowerTs: incoming.activeFPower != null ? incoming.activeFPowerTs : (prev?.activeFPowerTs ?? null),
              rmsVoltage: incoming.rmsVoltage ?? prev?.rmsVoltage ?? null,
              rmsCurrent: incoming.rmsCurrent ?? prev?.rmsCurrent ?? null,
              rmsCurrentTs: incoming.rmsCurrent != null ? incoming.rmsCurrentTs : (prev?.rmsCurrentTs ?? null),
              temperature: incoming.temperature ?? prev?.temperature ?? null,
            }));
          }
        })
        .catch(err => console.error('[WattelEM] Erreur polling meter values:', err));

      // Énergie de la session de recharge (transaction OCPP) :
      // = (dernière activeFEnergy dans meter_values pour la transaction)
      //   - (meter_start de cette transaction dans charging_transactions).
      // C'est la source PRIORITAIRE pour l'Énergie affichée (et non le MAX-MIN journalier).
      architectureApi.getDeviceSessionEnergy(deviceId)
        .then(res => { if (res.data !== undefined) setCatalogSessionEnergy(res.data); })
        .catch(err => console.error('[WattelEM] Erreur polling énergie session:', err));
    };

    const refreshHistory = () => {
      architectureApi.getMeterValuesHistory(deviceId, 30)
        .then(res => setCatalogDailyEnergy(res.data || []))
        .catch(err => console.error('[WattelEM] Erreur polling historique:', err));

      // Rafraîchir AUSSI l'historique filtré (celui qui pilote l'énergie affichée
      // + le graphe) — sinon l'Énergie reste figée jusqu'à un refresh de page,
      // car c'est `filteredCatalogEnergy` qui est prioritaire à l'affichage.
      const fetchFiltered = catalogTimeFilter.range === '24h'
        ? architectureApi.getMeterValuesHourly(deviceId, 24).then(r => r.data || [])
        : architectureApi.getMeterValuesHistory(deviceId, catalogTimeFilter.days, catalogTimeFilter.startDate, catalogTimeFilter.endDate).then(r => r.data || []);
      fetchFiltered
        .then(data => setFilteredCatalogEnergy(data))
        .catch(err => console.error('[WattelEM] Erreur polling historique filtré:', err));
    };

    // Immediate refresh on mount
    refreshLatest();
    refreshHistory();
    const latestInterval = setInterval(refreshLatest, 5000);
    const historyInterval = setInterval(refreshHistory, 10000);
    return () => {
      clearInterval(latestInterval);
      clearInterval(historyInterval);
    };
  }, [deviceIdFromUrl, catalogTimeFilter]);

  // Résoudre une BORNE OCPP quand le deviceId de l'URL n'est pas dans le catalogue MQTT.
  // On va chercher le device dans le device-service (base OCPP) + nom personnalisé (org_bornes)
  // et on synthétise un CatalogDevice → le bloc "mode catalogue" affiche les vraies valeurs
  // (tension/puissance via getLatestMeterValues, on/off via CatalogRelayControl, graphe via getMeterValuesHistory).
  useEffect(() => {
    const did = parseInt(deviceIdFromUrl ?? '', 10);
    if (isNaN(did)) { setBorneInfo(null); return; }
    // Déjà connu comme device catalogue MQTT → pas besoin de résolution borne
    if (catalogDevices.some(c => c.deviceId === did)) { setBorneInfo(null); return; }
    let cancelled = false;
    Promise.all([
      otaApi.getDevices().catch(() => ({ success: false, data: [] as any[] })),
      getOrgBornes().catch(() => ({ success: false, data: [] as any[] })),
    ]).then(([otaRes, orgRes]) => {
      if (cancelled) return;
      const otaList: any[] = (otaRes as any)?.data || [];
      const orgList: any[] = (orgRes as any)?.data || [];
      const dev = otaList.find(d => Number(d.device_id) === did);
      if (!dev) { setBorneInfo(null); return; }
      const sn = String(dev.device_sn);
      const orgName = orgList.find((b: any) => String(b.device_sn).toLowerCase() === sn.toLowerCase())?.borne_name;
      const s = sn.toLowerCase();
      const model = s.includes('go') ? 'WattelGo' : (s.includes('wall') || s.includes('wl')) ? 'WattelWall' : null;
      const status = String(dev.device_status || '').toLowerCase();
      setBorneInfo({
        deviceSn: sn,
        deviceName: orgName || dev.device_name || sn,
        deviceId: did,
        deviceStatus: dev.device_status || null,
        chargePointModel: model,
        chargePointVendor: 'OCPP',
        online: ['available', 'active', 'connected', 'preparing', 'charging'].includes(status),
      } as CatalogDevice);
    });
    return () => { cancelled = true; };
  }, [deviceIdFromUrl, catalogDevices]);

  // Polling de la session OCPP temps réel pour une BORNE (Wattel 22, WattelGo...).
  // Source identique à la page Bornes (charging_transactions + cache OCPP Redis) :
  // donne la VRAIE puissance instantanée (W) et l'énergie de session (kWh).
  // La table meter_values de l'architecture n'est pas fiable pour les bornes OCPP.
  useEffect(() => {
    const sn = borneInfo?.deviceSn;
    if (!sn) { setBorneSession(null); return; }
    let cancelled = false;
    const fetchSession = () => {
      getChargingSessionStatus([sn])
        .then(res => {
          if (cancelled) return;
          const s = (res.data || []).find(x => x.deviceSn?.toLowerCase() === sn.toLowerCase()) || (res.data || [])[0] || null;
          setBorneSession(s);
          // Mémorise la VRAIE énergie/puissance OCPP dans la session persistée
          // (localStorage) → après un refresh, on réaffiche la dernière valeur réelle
          // connue sans rien simuler. On NE supprime JAMAIS la session ici :
          // seul le bouton OFF arrête la charge (sinon on la perdrait au refresh,
          // car le backend ne renvoie une session que si une transaction est active).
          if (s && (Number(s.currentEnergyWh) > 0 || Number(s.currentPowerW) > 0)) {
            updateBorneSession(sn, {
              sessionEnergyWh: Number(s.currentEnergyWh) || 0,
              lastEnergyWh: Number(s.currentEnergyWh) || 0,
              transactionId: s.transactionId || getBorneSession(sn)?.transactionId || 0,
            });
          }
        })
        .catch(err => console.error('[WattelEM] Erreur session OCPP borne:', err));
    };
    fetchSession();
    const interval = setInterval(fetchSession, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [borneInfo?.deviceSn]);

  // Valeurs d'affichage pour une borne OCPP : on privilégie la session OCPP temps réel.
  // Puissance (kW) et énergie (kWh) réelles ; fallback sur meter_values sinon.
  const borneLivePowerKw = borneSession?.currentPowerW != null ? Number(borneSession.currentPowerW) / 1000 : null;
  const borneLiveEnergyKwh = borneSession?.currentEnergyKwh != null
    ? Number(borneSession.currentEnergyKwh)
    : (borneSession?.currentEnergyWh != null ? Number(borneSession.currentEnergyWh) / 1000 : null);

  // État de charge live — UNIQUEMENT les vraies données OCPP (aucune simulation).
  // La session persistée (localStorage) ne sert qu'à garder l'état "en charge"
  // après un refresh, le temps que le 1er poll OCPP confirme.
  const liveBorne = computeLiveBorne(
    borneInfo?.deviceSn ? getBorneSession(borneInfo.deviceSn) : null,
    borneSession,
  );
  void liveTick; // re-render forcé à l'appui ON/OFF (réactivité immédiate)

  // Réagit immédiatement quand le bouton ON/OFF (CatalogRelayControl) démarre/arrête
  // une session : on force un recalcul pour afficher/retirer l'état sans délai.
  // (Les VALEURS puissance/énergie, elles, viennent du poll OCPP toutes les 3s.)
  useEffect(() => {
    const onSessionChange = () => setLiveTick(x => (x + 1) % 1_000_000);
    window.addEventListener(BORNE_SESSION_EVENT, onSessionChange);
    window.addEventListener('storage', onSessionChange); // synchro inter-onglets
    return () => {
      window.removeEventListener(BORNE_SESSION_EVENT, onSessionChange);
      window.removeEventListener('storage', onSessionChange);
    };
  }, []);

  // Calculer le SN effectif à partir de snFromUrl ou deviceIdFromUrl
  const effectiveSn = (() => {
    if (snFromUrl) return snFromUrl;
    if (deviceIdFromUrl && catalogDevices.length > 0) {
      const catalogDevice = catalogDevices.find(c => c.deviceId === parseInt(deviceIdFromUrl, 10));
      if (catalogDevice?.deviceSn) {
        console.log('[WattelEM] deviceId', deviceIdFromUrl, '→ SN:', catalogDevice.deviceSn);
        return catalogDevice.deviceSn;
      }
    }
    return null;
  })();

  // Auto-sélectionner le device si SN passé dans l'URL (seulement pour les WattelEM, pas les devices catalogue)
  useEffect(() => {
    // Ne pas auto-sélectionner si on vient avec un deviceId (mode catalogue)
    if (deviceIdFromUrl) return;
    
    if (snFromUrl && devices.length > 0 && !selectedSn) {
      // Rechercher le device correspondant (case insensitive)
      const matchingDevice = devices.find(
        d => d.device_sn.toLowerCase() === snFromUrl.toLowerCase()
      );
      if (matchingDevice) {
        console.log('[WattelEM] Auto-sélection du device:', matchingDevice.device_sn);
        selectDevice(matchingDevice.device_sn);
      }
    }
  }, [snFromUrl, deviceIdFromUrl, devices, selectedSn, selectDevice]);

  // Filtrer les devices si un SN/deviceId est passé dans l'URL (depuis Architecture)
  // Note: On ne filtre QUE si on a un snFromUrl direct (venant du tableau électrique)
  // snFromUrl peut contenir plusieurs SNs séparés par des virgules
  // Si on a un deviceIdFromUrl, on va en mode catalogue
  const filteredDevices = (() => {
    let list = devices;
    // Filtre URL (depuis Architecture - tableau) — supporte plusieurs SNs séparés par virgule
    if (snFromUrl) {
      const snList = snFromUrl.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      return list.filter(d => snList.includes(d.device_sn.toLowerCase()));
    }
    // Tant que les installations ne sont pas chargées, on attend (ne pas tout afficher)
    if (!installationsLoaded) return [];
    // Filtre par installation (ou toutes installations si aucune sélectionnée)
    // Si installationDeviceSns est non-null, on ne montre QUE ces devices (liés à l'architecture)
    if (installationDeviceSns) {
      list = list.filter(d => installationDeviceSns.includes(d.device_sn.toLowerCase()));
    } else {
      // Pas de WattelEM dans l'architecture → liste vide (ne pas montrer tous les devices)
      list = [];
    }
    // Filtre par device sélectionné dans le dropdown
    if (filterDeviceSn) {
      list = list.filter(d => d.device_sn.toLowerCase() === filterDeviceSn.toLowerCase());
    }
    return list;
  })();

  // Info supplémentaire pour le device venant du catalogue (nom, type, etc.)
  // Retombe sur borneInfo (borne OCPP résolue depuis le device-service) si le deviceId
  // n'est pas un device catalogue MQTT.
  // Le libellé générique « Borne » est traduit à l'affichage (pas stocké dans l'état).
  const borneInfoDisplay = borneInfo && !borneInfo.chargePointModel
    ? { ...borneInfo, chargePointModel: t('eq_charger') }
    : borneInfo;
  const catalogInfo = (deviceIdFromUrl && catalogDevices.length > 0
    ? catalogDevices.find(c => c.deviceId === parseInt(deviceIdFromUrl, 10))
    : null) || borneInfoDisplay;

  // Mode catalogue: quand on vient avec deviceId (depuis un Point Électrique comme WattelGo, WattelWall)
  // OU en mode point simple (depuis un Point Électrique sans device)
  const isCatalogMode = !!deviceIdFromUrl || isSimplePointMode;

  // Tension du WattelEM lié à l'installation (pour WattelGo/WattelWall qui n'ont pas de mesure V)
  // On prend la tension du premier WattelEM de l'installation sélectionnée
  const linkedWattelemVoltage = (() => {
    if (!installationDeviceSns || devices.length === 0) return null;
    const linkedDevice = devices.find(d =>
      installationDeviceSns.includes(d.device_sn.toLowerCase()) && d.current?.voltage_v
    );
    return linkedDevice?.current?.voltage_v ? Number(linkedDevice.current.voltage_v) : null;
  })();

  // Tension effective pour WattelGo/WattelWall :
  // 1. rmsVoltage du meter si disponible et > 0
  // 2. Sinon tension du WattelEM lié
  // 3. Sinon 230V par défaut (standard réseau FR)
  const catalogVoltage = (() => {
    const fromMeter = catalogMeterData?.rmsVoltage ? Number(catalogMeterData.rmsVoltage) : 0;
    if (fromMeter > 10) return fromMeter; // valeur réelle du meter
    if (linkedWattelemVoltage && linkedWattelemVoltage > 10) return linkedWattelemVoltage;
    return 230; // défaut réseau FR
  })();

  // Énergie totale "meter values" = somme des daily_kwh de l'historique (même source que le graphe).
  // C'est la VRAIE énergie consommée cumulée, contrairement à la dernière ligne meter_values
  // (activeFEnergy instantané) qui peut être minuscule/nulle.
  const meterHistorySource = filteredCatalogEnergy.length > 0 ? filteredCatalogEnergy : catalogDailyEnergy;
  const meterHistoryKwh = meterHistorySource.reduce((s, r) => s + Number(r.daily_kwh || 0), 0);

  // Détection d'échelle DATA-DRIVEN (indépendante de l'ordre de résolution
  // des effets borneInfo/catalogDevices).
  //
  // - MQTT WattelEM    : registres bruts CT (ex: 8_800_000 = 0.88 kW) → /10_000_000
  // - OCPP borne (wattel22, etc.) : Watts/Wh directs (ex: 23 = 23 W) → /1000
  //
  // Heuristique : une valeur > 100_000 ne peut être que des registres bruts
  // (sinon ce serait > 100 kW, impossible pour ces équipements).
  const rawPower = catalogMeterData?.activeFPower != null ? Math.abs(Number(catalogMeterData.activeFPower)) : 0;
  const rawEnergy = catalogMeterData?.activeFEnergy != null ? Math.abs(Number(catalogMeterData.activeFEnergy)) : 0;
  const isRawCtScale = rawPower > 100_000 || rawEnergy > 100_000;
  const POWER_DIVISOR = isRawCtScale ? 10_000_000 : 1000;     // → kW
  const ENERGY_DIVISOR = isRawCtScale ? 10_000_000 : 1000;    // → kWh

  // Est-ce une BORNE OCPP (wattel22, WattelGo...) et non un WattelEM MQTT ?
  // borneInfo n'est résolu QUE pour un deviceId absent du catalogue MQTT.
  // → Le nouveau flux "énergie/puissance par session" ne s'applique QU'aux bornes OCPP.
  //   Le flux MQTT WattelEM reste strictement inchangé.
  const isOcppBorne = !!borneInfo;

  const POWER_FRESH_MS = 3 * 60 * 1000; // 3 min : au-delà, plus de charge en cours (OCPP)
  const meterPowerTs = catalogMeterData?.activeFPowerTs ? new Date(catalogMeterData.activeFPowerTs).getTime() : 0;
  const meterPowerFresh = meterPowerTs > 0 && (nowTick - meterPowerTs) < POWER_FRESH_MS;

  // Puissance (kW) affichée.
  const meterPowerKwRaw = (catalogMeterData?.activeFPower != null && Number(catalogMeterData.activeFPower) !== 0)
    ? Number(catalogMeterData.activeFPower) / POWER_DIVISOR
    : 0;
  const ocppSessionPowerKw = liveBorne.isCharging ? liveBorne.powerW / 1000 : 0;
  const catalogPowerKw = isOcppBorne
    // OCPP : puissance INSTANTANÉE — rejetée si la dernière mesure n'est pas récente
    // (sinon une valeur figée de plusieurs heures, ex 0.682 kW, resterait affichée).
    ? Math.max(
        ocppSessionPowerKw,
        meterPowerFresh ? meterPowerKwRaw : 0,
        liveBorne.isCharging ? (borneLivePowerKw ?? 0) : 0,
      )
    // MQTT : flux inchangé — meilleure valeur disponible.
    : Math.max(ocppSessionPowerKw, meterPowerKwRaw, borneLivePowerKw ?? 0);

  // Énergie (kWh) affichée.
  const sessionEnergyKwh = catalogSessionEnergy?.energyKwh != null ? Number(catalogSessionEnergy.energyKwh) : null;
  const meterRawEnergyKwh = (catalogMeterData?.activeFEnergy != null)
    ? Number(catalogMeterData.activeFEnergy) / ENERGY_DIVISOR
    : 0;
  const ocppSessionEnergyKwh = liveBorne.isCharging ? liveBorne.energyWh / 1000 : 0;
  // OCPP : flux par session — on incrémente PENDANT la recharge
  //   (énergie session = dernière activeFEnergy meter_values - meter_start, calculée backend)
  //   et on retombe à 0 dès que la session est arrêtée / terminée.
  // IMPORTANT : ne JAMAIS retomber sur meterRawEnergyKwh ici — activeFEnergy est un
  // registre CUMULÉ à vie (ex: 20807 Wh) qui afficherait 20.810 kWh au lieu de l'énergie
  // de session. Repli uniquement sur les sources "session" (OCPP live).
  const isCharging = meterPowerFresh || liveBorne.isCharging || (catalogSessionEnergy?.isActive ?? false);
  const catalogEnergyKwh = isOcppBorne
    ? (!isCharging
        ? 0
        : (sessionEnergyKwh != null
            ? sessionEnergyKwh
            : Math.max(ocppSessionEnergyKwh, borneLiveEnergyKwh ?? 0)))
    // MQTT : flux inchangé — historique cumulé > session OCPP live > meter_values brute.
    : (meterHistoryKwh > 0
        ? meterHistoryKwh + ocppSessionEnergyKwh
        : Math.max(meterRawEnergyKwh, ocppSessionEnergyKwh, borneLiveEnergyKwh ?? 0));

  // Courant affiché (Ampères) :
  // 1. rmsCurrent du meter si disponible (> 0) — VRAIE mesure OCPP
  // 2. Sinon calcul I = P / V à partir de la puissance et tension affichées
  const catalogCurrent = (() => {
    // OCPP : rmsCurrent (dernière valeur non-nulle sur 24h) rejeté s'il n'est pas récent.
    // MQTT : flux inchangé (on prend la valeur dès qu'elle est > 0).
    const currentTs = catalogMeterData?.rmsCurrentTs ? new Date(catalogMeterData.rmsCurrentTs).getTime() : 0;
    const currentFresh = currentTs > 0 && (nowTick - currentTs) < POWER_FRESH_MS;
    const meterCurrentOk = isOcppBorne ? currentFresh : true;
    const meterCurrent = (meterCurrentOk && catalogMeterData?.rmsCurrent != null) ? Number(catalogMeterData.rmsCurrent) : 0;
    if (meterCurrent > 0) return meterCurrent;
    const powerW = catalogPowerKw * 1000;
    if (powerW <= 0 || catalogVoltage <= 0) return 0;
    return powerW / catalogVoltage;
  })();

  // Icône selon le type de point
  const getPointIcon = (type: string | null) => {
    switch (type) {
      case 'prise': return '🔌';
      case 'lumiere': return '💡';
      case 'chauffage': return '🔥';
      case 'clim': return '❄️';
      case 'chauffeeau': return '🚿';
      case 'four': return '🍳';
      case 'lave-linge': return '🧺';
      case 'voiture': return '🚗';
      default: return '🔌';
    }
  };

  console.log('[WattelEM] Mode:', { 
    snFromUrl,
    deviceIdFromUrl,
    pointNameFromUrl,
    isSimplePointMode,
    catalogDevicesCount: catalogDevices.length,
    catalogInfo: catalogInfo?.deviceName, 
    filteredDevicesCount: filteredDevices.length,
    isCatalogMode,
    catalogMeterData 
  });

  return (
    <div className="space-y-6">
      {/* ZCE Header - Hero technique premium (circuit / modules électriques) */}
      <div className="relative overflow-hidden rounded-3xl shadow-2xl zce-fade-in bg-gradient-to-br from-[#0b1e3f] via-[#11305f] to-[#0a1a35]">
        {/* Motif circuit imprimé en SVG */}
        <svg
          className="pointer-events-none absolute inset-0 w-full h-full opacity-[0.18]"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid slice"
          viewBox="0 0 1600 520"
          aria-hidden="true"
        >
          <defs>
            <pattern id="wem-circuit" x="0" y="0" width="160" height="160" patternUnits="userSpaceOnUse">
              <path d="M0 40 H60 V0 M100 0 V40 H160 M0 120 H60 V160 M100 160 V120 H160 M40 60 V100 H120 V60 Z"
                fill="none" stroke="#7dd3fc" strokeWidth="1.2" />
              <circle cx="60" cy="40" r="3" fill="#22d3ee" />
              <circle cx="100" cy="40" r="3" fill="#22d3ee" />
              <circle cx="60" cy="120" r="3" fill="#22d3ee" />
              <circle cx="100" cy="120" r="3" fill="#22d3ee" />
              <rect x="55" y="75" width="50" height="10" rx="1" fill="none" stroke="#a5f3fc" strokeWidth="0.8" />
            </pattern>
            <linearGradient id="wem-glow" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#wem-circuit)" />
          <rect width="100%" height="100%" fill="url(#wem-glow)" />
        </svg>

        {/* Icônes modules électriques décoratives */}
        <svg
          className="pointer-events-none absolute right-0 top-0 h-full w-1/2 opacity-25"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 800 520"
          aria-hidden="true"
        >
          {/* Disjoncteurs en rangée */}
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <g key={i} transform={`translate(${120 + i * 90}, 180)`}>
              <rect x="0" y="0" width="60" height="160" rx="6" fill="#0f2a52" stroke="#38bdf8" strokeWidth="1.5" />
              <rect x="10" y="20" width="40" height="50" rx="3" fill="#1e3a8a" stroke="#7dd3fc" strokeWidth="1" />
              <rect x="22" y="35" width="16" height="20" rx="2" fill="#22d3ee" />
              <circle cx="30" cy="100" r="4" fill="#10b981" />
              <rect x="15" y="115" width="30" height="3" fill="#7dd3fc" />
              <rect x="15" y="125" width="30" height="3" fill="#7dd3fc" />
              <rect x="15" y="135" width="30" height="3" fill="#7dd3fc" />
            </g>
          ))}
          {/* Câbles connectant les modules en bas */}
          <path d="M150 380 Q400 420 650 380" fill="none" stroke="#22d3ee" strokeWidth="2" opacity="0.6" />
          <path d="M150 400 Q400 440 650 400" fill="none" stroke="#10b981" strokeWidth="2" opacity="0.5" />
        </svg>

        {/* Halos lumineux */}
        <div className="pointer-events-none absolute -top-24 -left-24 w-80 h-80 rounded-full bg-cyan-500/30 blur-3xl zce-float-orb" />
        <div className="pointer-events-none absolute -bottom-32 -right-16 w-96 h-96 rounded-full bg-blue-500/25 blur-3xl zce-float-orb" style={{ animationDelay: '-6s' }} />
        <div className="pointer-events-none absolute top-1/3 left-1/2 w-64 h-64 rounded-full bg-emerald-500/15 blur-3xl zce-float-orb" style={{ animationDelay: '-12s' }} />

        {/* Voile dégradé pour assurer la lisibilité du texte à gauche */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0b1e3f]/95 via-[#0b1e3f]/70 to-transparent" />
        <div className="pointer-events-none absolute inset-0 zce-shimmer" />

        <div className="relative p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4 zce-fade-up">
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-400/40 rounded-xl blur-md zce-pulse-dot" />
                <img
                  src="/logos/logo zce.png"
                  alt={t('eq3_zce_logo_alt')}
                  className="relative h-12 w-auto object-contain bg-white rounded-xl p-1.5 shadow-lg"
                />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 zce-pulse-dot" />
                    {t('eq3_live_badge')}
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-heading tracking-tight leading-tight">
                  <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-emerald-300 bg-clip-text text-transparent">{t('eq_title')}</span>
                </h1>
                <p className="text-blue-200/80 text-sm mt-0.5">
                  {t('eq_subtitle')}
                  {lastUpdate && (
                    <span className="ml-2 text-blue-300/60 text-xs">
                      • {lastUpdate.toLocaleTimeString(locale)}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap zce-fade-up zce-delay-1">
              {selectedInstallationId && (() => {
                const inst = installations.find(i => i.id === selectedInstallationId);
                const count = inst ? inst.zones.reduce((s, z) => s + (z.equipment?.length ?? 0), 0) : 0;
                return (
                  <div className="bg-white/10 backdrop-blur-xl rounded-xl px-4 py-2 text-white border border-white/20">
                    <span className="text-2xl font-extrabold font-heading tabular-nums">{count}</span>
                    <span className="text-sm ml-1 text-blue-200">{tp('eq_deviceCount', count)}</span>
                  </div>
                );
              })()}
              <button
                onClick={loadOverview}
                disabled={loading}
                className="px-5 py-2.5 text-sm font-semibold bg-zce-orange text-zce-navy rounded-xl hover:bg-zce-orange-light disabled:opacity-50 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 group"
              >
                <svg className={`w-4 h-4 transition-transform group-hover:rotate-180 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {loading ? t('eq_loading') : t('eq_refresh')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="zce-alert-error">
          <svg className="w-5 h-5 text-rose-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-rose-700">
            <p className="font-semibold">{t('eq_connectionError')}</p>
            <p className="mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && devices.length === 0 && (
        <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-16 text-center">
          <div className="w-16 h-16 mx-auto mb-4 relative">
            <div className="absolute inset-0 rounded-full border-4 border-zce-gray-light"></div>
            <div className="absolute inset-0 rounded-full border-4 border-zce-blue border-t-transparent animate-spin"></div>
          </div>
          <p className="text-zce-navy font-semibold text-lg">{t('eq_loadingDevices')}</p>
          <p className="text-zce-gray-muted text-sm mt-1">{t('eq_connectingToServer')}</p>
        </div>
      )}

      {/* No devices — inclut le cas "1er chargement en erreur" (sinon page blanche) */}
      {!loading && devices.length === 0 && !isCatalogMode && (
        <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-16 text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-zce-gray-light rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-zce-gray-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          </div>
          {error ? (
            <>
              <p className="text-xl font-semibold text-zce-navy mb-2 font-heading">{t('eq_dataUnavailable')}</p>
              <p className="text-zce-gray-muted mb-5">
                {t('eq_serverNoResponse')}
              </p>
              <button
                onClick={loadOverview}
                className="px-5 py-2.5 text-sm font-semibold bg-zce-blue text-white rounded-xl hover:bg-blue-700 transition-colors"
              >
                {t('eq_retryNow')}
              </button>
            </>
          ) : (
            <>
              <p className="text-xl font-semibold text-zce-navy mb-2 font-heading">{t('eq_noDeviceFound')}</p>
              <p className="text-zce-gray-muted">{t('eq_checkWattelemConfig')}</p>
            </>
          )}
        </div>
      )}

      {/* Mode Catalogue: affichage d'un device du catalogue (WattelGo, WattelWall, etc.) - Même layout que WattelEM */}
      {isCatalogMode && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: filters + device card */}
          <div className="space-y-4">
            {/* ── Filtres (always visible) ── */}
            <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-zce-navy uppercase tracking-wider flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-zce-orange"></span>
                  {t('eq_filters')}
                </h2>
                <button
                  onClick={() => {
                    const instParam = selectedInstallationId ? `?installationId=${selectedInstallationId}` : '';
                    window.location.href = `/wattel-em${instParam}`;
                  }}
                  className="text-xs text-zce-blue hover:text-blue-700 font-medium transition-colors"
                >
                  {t('eq_all')}
                </button>
              </div>

              {/* Installation dropdown */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-zce-orange/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-zce-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <span className="text-xs font-semibold text-zce-navy uppercase tracking-wider">{t('home_installation')}</span>
                </div>
                <select
                  value={selectedInstallationId ?? ''}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : null;
                    setSelectedInstallationId(val);
                    setFilterDeviceSn(null);
                    window.location.href = val ? `/wattel-em?installationId=${val}` : '/wattel-em';
                  }}
                  className="w-full text-sm bg-zce-gray-light border border-gray-200 rounded-lg px-3 py-2.5 text-zce-navy font-medium focus:outline-none focus:ring-2 focus:ring-zce-blue/50 focus:border-zce-blue transition-all appearance-none cursor-pointer"
                >
                  <option value="">{t('eq_allInstallations')}</option>
                  {installations.map(inst => (
                    <option key={inst.id} value={inst.id}>{inst.name}</option>
                  ))}
                </select>
              </div>

              {/* Device dropdown */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-zce-blue/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-zce-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  </div>
                  <span className="text-xs font-semibold text-zce-navy uppercase tracking-wider">{t('eq_equipment')}</span>
                </div>
                <select
                  value={deviceIdFromUrl ? `catalog:${deviceIdFromUrl}` : (snFromUrl ?? filterDeviceSn ?? '')}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    setFilterDeviceSn(val);
                    if (val && val.startsWith('catalog:')) {
                      const catId = val.replace('catalog:', '');
                      const instParam = selectedInstallationId ? `&installationId=${selectedInstallationId}` : '';
                      window.location.href = `/wattel-em?deviceId=${catId}${instParam}`;
                    } else if (val && val.startsWith('point:')) {
                      const pointId = val.replace('point:', '');
                      const inst = installations.find(i => i.id === selectedInstallationId);
                      const eq = inst?.zones.flatMap(z => z.equipment || []).find(e => String(e.id) === pointId);
                      if (eq) navigateToPoint(eq);
                    } else if (val) {
                      const instParam = selectedInstallationId ? `&installationId=${selectedInstallationId}` : '';
                      window.location.href = `/wattel-em?sn=${val}${instParam}`;
                    }
                  }}
                  disabled={!selectedInstallationId}
                  className="w-full text-sm bg-zce-gray-light border border-gray-200 rounded-lg px-3 py-2.5 text-zce-navy font-medium focus:outline-none focus:ring-2 focus:ring-zce-blue/50 focus:border-zce-blue disabled:opacity-50 disabled:cursor-not-allowed transition-all appearance-none cursor-pointer"
                >
                  <option value="">
                    {selectedInstallationId ? t('eq_allDevices') : t('eq_selectInstallation')}
                  </option>
                  {selectedInstallationId && (() => {
                    const inst = installations.find(i => i.id === selectedInstallationId);
                    if (!inst) return null;
                    const wattelemSns = inst.zones
                      .flatMap(z => (z.wattelemSn || '').split(',').map(s => s.trim()).filter(Boolean));
                    const catalogDeviceIds = inst.zones
                      .flatMap(z => z.equipment || [])
                      .map(eq => eq.deviceId)
                      .filter((id): id is number => id != null && id > 0);
                    const uniqueCatalogIds = [...new Set(catalogDeviceIds)];
                    const uniqueSns = [...new Set(wattelemSns)];
                    return (
                      <>
                        {uniqueSns.map(sn => {
                          const dev = devices.find(d => d.device_sn.toLowerCase() === sn.toLowerCase());
                          return (
                            <option key={`em-${sn}`} value={sn}>
                              {displaySn(sn)} {dev ? (dev.online ? `● ${t('eq_online')}` : `○ ${t('eq_offline')}`) : ''}
                            </option>
                          );
                        })}
                        {uniqueCatalogIds.map(id => {
                          const cat = catalogDevices.find(c => c.deviceId === id);
                          return (
                            <option key={`cat-${id}`} value={`catalog:${id}`}>
                              {cat ? (displaySn(cat.deviceName || cat.deviceSn) || t('eq3_device_number', { id })) : t('eq3_device_number', { id })}
                              {cat?.chargePointModel ? ` (${cat.chargePointModel})` : ''}
                            </option>
                          );
                        })}
                        {inst.zones.flatMap(z => z.equipment || [])
                          .filter(eq => !eq.deviceId || eq.deviceId <= 0)
                          .map(eq => {
                            const sn = (eq as any).deviceSn as string | null | undefined;
                            if (sn) {
                              return (
                                <option key={`mqtt-${eq.id}`} value={sn}>
                                  {displayDefaultName(eq.name, t, tp)} ● MQTT
                                </option>
                              );
                            }
                            return (
                              <option key={`point-${eq.id}`} value={`point:${eq.id}`}>
                                {displayDefaultName(eq.name, t, tp)} ● {t('eq_calculated')}
                              </option>
                            );
                          })}
                      </>
                    );
                  })()}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-zce-navy to-zce-blue flex items-center justify-center">
                <img src="/logos/logo zce.png" alt="ZCE" className="h-5 w-auto object-contain" />
              </div>
              <h2 className="text-sm font-bold text-zce-navy uppercase tracking-wider">
                {t('eq_equipment')}
              </h2>
            </div>

            {/* Carte du device catalogue OU point simple (style comme DeviceCard) */}
            {isSimplePointMode ? (
              // Mode Point Simple (sans device réel - données calculées)
              <div className="w-full text-left p-5 rounded-xl border-2 border-pink-500 bg-gradient-to-br from-pink-50 to-white shadow-zce-md">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-zce-navy text-sm truncate">
                    {displayDefaultName(pointNameFromUrl, t, tp) || t('eq_electricalPoint')}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-pink-100 text-pink-700">
                    <span className="w-2 h-2 rounded-full bg-pink-500"></span>
                    {t('eq_calculated')}
                  </span>
                </div>
                
                {/* Valeurs calculées */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-orange-50 rounded-lg p-2 text-center">
                    <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">{t('eq_power')}</span>
                    <span className="font-bold text-zce-orange">
                      {displayedDeducedPowerW > 0 ? `${(displayedDeducedPowerW / 1000).toFixed(3)} kW` : '—'}
                    </span>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-2 text-center">
                    <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">{t('eq_energy')}</span>
                    <span className="font-bold text-emerald-600">
                      {displayedDeducedEnergyWh > 0 ? `${(displayedDeducedEnergyWh / 1000).toFixed(3)} kWh` : '—'}
                    </span>
                  </div>
                </div>
                
                {/* Type de device */}
                <div className="mt-3 text-xs text-zce-gray-muted truncate border-t border-gray-100 pt-2 flex items-center gap-2">
                  <span className="text-lg">{getPointIcon(pointTypeFromUrl)}</span>
                  <span>{t('eq_calculatedPointNoDevice')}</span>
                </div>
              </div>
            ) : catalogInfo ? (
              <div className="w-full text-left p-5 rounded-xl border-2 border-zce-blue bg-gradient-to-br from-blue-50 to-white shadow-zce-md">
                {/* Vignette + nom */}
                <div className="flex items-center gap-3 mb-3">
                  {(() => {
                    const m = [catalogInfo.chargePointModel, catalogInfo.deviceName, catalogInfo.deviceSn].filter(Boolean).join(' ').toLowerCase();
                    const img = m.includes('go') ? '/devices/wattelgo.svg' : (m.includes('wall') || m.includes('wl')) ? '/devices/wattelwall.svg' : null;
                    return img ? (
                      <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-sky-100 to-blue-50 border border-sky-200 flex items-center justify-center p-1.5 shadow-sm">
                        <img src={img} alt={catalogInfo.chargePointModel || ''} className="h-full w-full object-contain" />
                      </div>
                    ) : null;
                  })()}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-semibold text-zce-navy text-sm truncate">
                        {catalogInfo.deviceName ? displaySn(catalogInfo.deviceName) : (catalogInfo.chargePointModel || t('adm_ota_th_device'))}
                      </span>
                      <span className={`flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                        catalogInfo.deviceStatus === 'Available'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          catalogInfo.deviceStatus === 'Available' ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'
                        }`} />
                        {catalogInfo.deviceStatus === 'Available' ? t('eq_active') : catalogInfo.deviceStatus || t('eq_unknown')}
                      </span>
                    </div>
                    {catalogInfo.chargePointVendor && (
                      <p className="text-xs text-zce-gray-muted truncate">{catalogInfo.chargePointVendor} {catalogInfo.chargePointModel}</p>
                    )}
                  </div>
                </div>
                
                {/* Valeurs comme pour WattelEM */}
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div className="bg-zce-gray-light rounded-lg p-2 text-center">
                    <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">V</span>
                    <span className="font-bold text-zce-navy">
                      {catalogVoltage.toFixed(1)}
                    </span>
                  </div>
                  <div className="bg-zce-gray-light rounded-lg p-2 text-center">
                    <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">A</span>
                    <span className="font-bold text-zce-navy">
                      {catalogCurrent.toFixed(2)}
                    </span>
                  </div>
                  <div className="bg-zce-gray-light rounded-lg p-2 text-center">
                    <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">kW</span>
                    <span className="font-bold text-zce-orange">
                      {catalogMeterData?.activeFPower != null ? (Number(catalogMeterData.activeFPower) / 10_000_000).toFixed(3) : '—'}
                    </span>
                  </div>
                </div>
                
                {catalogMeterData?.timestamp && (
                  <div className="mt-1 text-xs text-zce-gray-muted flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {timeAgo(catalogMeterData.timestamp, t)}
                  </div>
                )}
              </div>
            ) : catalogLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin h-8 w-8 border-4 border-zce-blue border-t-transparent rounded-full mx-auto mb-3"></div>
                <p className="text-zce-gray-muted text-sm">{t('eq_loading')}</p>
              </div>
            ) : null}
          </div>

          {/* Right: détails (2 colonnes comme pour WattelEM) */}
          <div className="lg:col-span-2 space-y-6">
            {isSimplePointMode ? (
              // Mode Point Simple - afficher les infos calculées
              <>
                <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-lg bg-pink-500 flex items-center justify-center">
                      <span className="text-xl text-white">{getPointIcon(pointTypeFromUrl)}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-zce-navy font-heading">{t('eq_pointInfo')}</h3>
                      <p className="text-xs text-zce-gray-muted">{t('eq_estimatedDataFor', { name: displayDefaultName(pointNameFromUrl, t, tp) })}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">⚡</span>
                        <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('eq_power')}</span>
                      </div>
                      <p className="text-2xl font-bold text-zce-orange">
                        {displayedDeducedPowerW > 0
                          ? <>{(displayedDeducedPowerW / 1000).toFixed(3)} <span className="text-sm font-normal">kW</span></>
                          : <>— <span className="text-sm font-normal">kW</span></>}
                      </p>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">🔋</span>
                        <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('eq_energy')}</span>
                      </div>
                      <p className="text-2xl font-bold text-emerald-600">
                        {displayedDeducedEnergyWh > 0
                          ? <>{(displayedDeducedEnergyWh / 1000).toFixed(3)} <span className="text-sm font-normal">kWh</span></>
                          : <>— <span className="text-sm font-normal">kWh</span></>}
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{getPointIcon(pointTypeFromUrl)}</span>
                        <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('arch_type')}</span>
                      </div>
                      <p className="text-2xl font-bold text-purple-600 capitalize">
                        {pointTypeLabel(pointTypeFromUrl, t)}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">📊</span>
                        <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('adm_rse_th_source')}</span>
                      </div>
                      <p className="text-lg font-bold text-zce-blue">
                        {displayedDeducedEnergyWh > 0 ? t('eq_wattelemDeduced') : t('eq_calculated')}
                      </p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">🌡️</span>
                        <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('eq_temperature')}</span>
                      </div>
                      <p className="text-2xl font-bold text-red-500">
                        — <span className="text-sm font-normal">°C</span>
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">🌿</span>
                        <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('eq_co2Emission')}</span>
                      </div>
                      <p className="text-2xl font-bold text-green-700">
                        — <span className="text-sm font-normal">kg</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Info Card */}
                <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-zce-gray-light flex items-center justify-center">
                      <span className="text-lg">ℹ️</span>
                    </div>
                    <h3 className="text-lg font-semibold text-zce-navy font-heading">{t('eq_information')}</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-zce-gray-muted uppercase tracking-wider mb-1">{t('eq_name')}</p>
                      <p className="font-semibold text-zce-navy">{displayDefaultName(pointNameFromUrl, t, tp) || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zce-gray-muted uppercase tracking-wider mb-1">{t('arch_type')}</p>
                      <p className="font-semibold text-zce-navy capitalize">{pointTypeLabel(pointTypeFromUrl, t)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zce-gray-muted uppercase tracking-wider mb-1">{t('eq_power')}</p>
                      <p className="font-semibold text-zce-navy">
                        {displayedDeducedPowerW > 0
                          ? t('eq_powerDeduced', { v: (displayedDeducedPowerW / 1000).toFixed(3) })
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-zce-gray-muted uppercase tracking-wider mb-1">{t('adm_ota_th_device')}</p>
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700">
                        <span className="w-2 h-2 rounded-full bg-yellow-500" />
                        {t('eq_unassigned')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-zce-gray-muted">
                    <p>{t('eq_pointNoDeviceInfo')}</p>
                    <p className="mt-2">{t('eq_pointAssignHint')}</p>
                  </div>
                </div>
              </>
            ) : catalogInfo ? (
              <>
                {/* Télémétrie Grid - Même style que TelemetryGrid */}
                <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-lg bg-zce-navy flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-zce-navy font-heading">{t('eq_realtimeTelemetry')}</h3>
                      <p className="text-xs text-zce-gray-muted">{t('eq_instantReadingsOf', { model: catalogInfo.chargePointModel || t('eq_deviceFallback') })}</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      {t('eq3_live_refresh_5s')}
                      {lastMeterRefresh && (
                        <span className="text-emerald-600 font-normal ml-1">
                          {t('eq_agoSecondsParen', { n: Math.max(0, Math.floor((nowTick - lastMeterRefresh.getTime()) / 1000)) })}
                        </span>
                      )}
                    </span>
                  </div>

                  {catalogLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin h-8 w-8 border-4 border-zce-blue border-t-transparent rounded-full mx-auto mb-3"></div>
                      <p className="text-zce-gray-muted">{t('eq_loadingData')}</p>
                    </div>
                  ) : (catalogMeterData || borneInfo) ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">⚡</span>
                          <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('eq_voltage')}</span>
                        </div>
                        <p className="text-2xl font-bold text-zce-blue">
                          {catalogVoltage.toFixed(1)}
                          <span className="text-sm font-normal">V</span>
                          {linkedWattelemVoltage && Number(catalogMeterData?.rmsVoltage || 0) <= 10 && (
                            <span className="text-xs text-zce-gray-muted ml-1">(WattelEM)</span>
                          )}
                        </p>
                      </div>
                      <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">〰️</span>
                          <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('eq_current')}</span>
                        </div>
                        <p className="text-2xl font-bold text-zce-orange">
                          {catalogCurrent.toFixed(3)}
                          <span className="text-sm font-normal">A</span>
                          {catalogCurrent > 0 && (
                            <span className="text-xs text-zce-gray-muted ml-1">
                              {catalogMeterData?.rmsCurrent != null && Number(catalogMeterData.rmsCurrent) > 0 ? '(OCPP)' : t('eq_calculatedParen')}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">⚡</span>
                          <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('eq_power')}</span>
                        </div>
                        <p className="text-2xl font-bold text-zce-navy">
                          {catalogPowerKw.toFixed(3)} <span className="text-sm font-normal">kW</span>
                          {(catalogMeterData?.activeFPower == null || Number(catalogMeterData.activeFPower) === 0) && borneLivePowerKw != null && borneLivePowerKw > 0 && (
                            <span className="text-xs text-zce-gray-muted ml-1">(OCPP)</span>
                          )}
                        </p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">φ</span>
                          <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('eq_powerFactorShort')}</span>
                        </div>
                        <p className="text-2xl font-bold text-purple-600">
                          — <span className="text-sm font-normal"></span>
                        </p>
                      </div>
                      <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">∿</span>
                          <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('eq_frequency')}</span>
                        </div>
                        <p className="text-2xl font-bold text-teal-600">
                          — <span className="text-sm font-normal">Hz</span>
                        </p>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">📊</span>
                          <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('eq_energy')}</span>
                          {sessionEnergyKwh != null && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${catalogSessionEnergy?.isActive ? 'bg-emerald-200 text-emerald-800' : 'bg-zce-gray-100 text-zce-gray-muted'}`}>
                              {catalogSessionEnergy?.isActive ? t('eq_sessionLive') : t('eq_lastSession')}
                            </span>
                          )}
                        </div>
                        <p className="text-2xl font-bold text-emerald-600">
                          {catalogEnergyKwh.toFixed(3)} <span className="text-sm font-normal">kWh</span>
                        </p>
                      </div>
                      <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">🌡️</span>
                          <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('eq_temperature')}</span>
                        </div>
                        <p className="text-2xl font-bold text-red-500">
                          {catalogMeterData?.temperature != null && Number(catalogMeterData.temperature) !== 0
                            ? Number(catalogMeterData.temperature).toFixed(1)
                            : '—'} <span className="text-sm font-normal">°C</span>
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">🌿</span>
                          <span className="text-xs text-zce-gray-muted uppercase tracking-wider">{t('eq_co2Emission')}</span>
                        </div>
                        <p className="text-2xl font-bold text-green-700">
                          {(catalogEnergyKwh * 0.052).toFixed(3)} <span className="text-sm font-normal">kg</span>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-zce-gray-muted">{t('eq_noTelemetryAvailable')}</p>
                    </div>
                  )}
                </div>

                {/* Graphique de consommation */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-zce-navy to-zce-blue flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-zce-navy font-heading">{t('eq_energyReport')}</h2>
                        <p className="text-xs text-zce-gray-muted">{t('eq_energyReportSubtitle')}</p>
                      </div>
                    </div>
                    <ChartTimeFilter value={catalogTimeFilter} onChange={setCatalogTimeFilter} />
                  </div>
                  <EnergyOverTimeChart
                    data={filteredCatalogEnergy.length > 0 || filteredCatalogLoading ? filteredCatalogEnergy : catalogDailyEnergy}
                    loading={filteredCatalogLoading || catalogEnergyLoading}
                    title={t('eq_consumptionOf', { name: catalogInfo.deviceName ? displaySn(catalogInfo.deviceName) : displaySn(catalogInfo.deviceSn) || t('adm_ota_th_device') })}
                    hourly={catalogTimeFilter.range === '24h'}
                    rangeDays={catalogTimeFilter.range === 'custom' ? undefined : catalogTimeFilter.days}
                    rangeStartDate={catalogTimeFilter.range === 'custom' ? catalogTimeFilter.startDate : undefined}
                    rangeEndDate={catalogTimeFilter.range === 'custom' ? catalogTimeFilter.endDate : undefined}
                  />
                </div>

                {/* Informations du device - Style comme ZoneInfo */}
                <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-lg bg-white border border-zce-blue/30 flex items-center justify-center overflow-hidden">
                      {(() => {
                        const mInfo = [catalogInfo.chargePointModel, catalogInfo.deviceName, catalogInfo.deviceSn].filter(Boolean).join(' ').toLowerCase();
                        if (mInfo.includes('go')) return <img src="/devices/wattelgo.svg" alt="WattelGo" className="h-9 w-9 object-contain" />;
                        if (mInfo.includes('wall') || mInfo.includes('wl')) return <img src="/devices/wattelwall.svg" alt="WattelWall" className="h-9 w-9 object-contain" />;
                        return <span className="text-xl">📱</span>;
                      })()}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-zce-navy font-heading">{t('eq_deviceInfo')}</h3>
                      <p className="text-xs text-zce-gray-muted">{catalogInfo.chargePointVendor} {catalogInfo.chargePointModel}</p>
                    </div>
                  </div>
                  
                  <div className="border border-gray-100 rounded-xl p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-zce-gray-muted uppercase tracking-wider mb-1">{t('eq_deviceIdLabel')}</p>
                        <p className="font-semibold text-zce-navy">{catalogInfo.deviceId}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zce-gray-muted uppercase tracking-wider mb-1">{t('eq_name')}</p>
                        <p className="font-semibold text-zce-navy">{catalogInfo.deviceName ? displaySn(catalogInfo.deviceName) : '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zce-gray-muted uppercase tracking-wider mb-1">{t('eq_serialNumber')}</p>
                        <p className="font-semibold text-zce-navy text-xs">{displaySn(catalogInfo.deviceSn) || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zce-gray-muted uppercase tracking-wider mb-1">{t('eq_status')}</p>
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                          catalogInfo.deviceStatus === 'Available' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <span className={`w-2 h-2 rounded-full ${
                            catalogInfo.deviceStatus === 'Available' ? 'bg-emerald-500' : 'bg-gray-400'
                          }`} />
                          {catalogInfo.deviceStatus || t('eq_unknown')}
                        </span>
                      </div>
                    </div>
                    
                    {catalogMeterData?.timestamp && (
                      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 text-sm text-zce-gray-muted">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t('eq_lastUpdateAt', { date: new Date(catalogMeterData.timestamp).toLocaleString(locale) })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Contrôle Relay OCPP (start/stop transaction) */}
                {catalogInfo.deviceSn && (
                  <CatalogRelayControl
                    deviceSn={catalogInfo.deviceSn}
                    isConnected={['available', 'preparing', 'active', 'connected', 'online', 'charging'].includes((catalogInfo.deviceStatus || '').toLowerCase())}
                  />
                )}
              </>
            ) : (
              <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-16 text-center">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-zce-gray-light to-gray-200 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-zce-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-zce-navy mb-2 font-heading">{t('eq_loadingDevice')}</h3>
                <p className="text-zce-gray-muted">{t('eq_fetchingCatalog')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main layout - seulement si pas en mode catalogue */}
      {(filteredDevices.length > 0 || (devices.length > 0 && !isCatalogMode)) && !isCatalogMode && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: device list */}
          <div className="space-y-4">
            {/* ── Filtres ── */}
            {(
              <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-bold text-zce-navy uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-zce-orange"></span>
                    {t('eq_filters')}
                  </h2>
                  {(selectedInstallationId || filterDeviceSn) && (
                    <button
                      onClick={() => {
                        const instParam = selectedInstallationId ? `?installationId=${selectedInstallationId}` : '';
                        window.location.href = `/wattel-em${instParam}`;
                      }}
                      className="text-xs text-zce-blue hover:text-blue-700 font-medium transition-colors"
                    >
                      {t('eq_all')}
                    </button>
                  )}
                </div>

                {/* Installation dropdown */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-zce-orange/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-zce-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <span className="text-xs font-semibold text-zce-navy uppercase tracking-wider">{t('home_installation')}</span>
                  </div>
                  <select
                    value={selectedInstallationId ?? ''}
                    onChange={(e) => {
                      const val = e.target.value ? Number(e.target.value) : null;
                      window.location.href = val ? `/wattel-em?installationId=${val}` : '/wattel-em';
                    }}
                    className="w-full text-sm bg-zce-gray-light border border-gray-200 rounded-lg px-3 py-2.5 text-zce-navy font-medium focus:outline-none focus:ring-2 focus:ring-zce-blue/50 focus:border-zce-blue transition-all appearance-none cursor-pointer"
                  >
                    <option value="">{t('eq_allInstallations')}</option>
                    {installations.map(inst => (
                      <option key={inst.id} value={inst.id}>{inst.name}</option>
                    ))}
                  </select>
                </div>

                {/* Device dropdown */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-zce-blue/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-zce-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                      </svg>
                    </div>
                    <span className="text-xs font-semibold text-zce-navy uppercase tracking-wider">{t('adm_ota_th_device')}</span>
                  </div>
                  <select
                    value={deviceIdFromUrl ? `catalog:${deviceIdFromUrl}` : (snFromUrl ?? filterDeviceSn ?? '')}
                    onChange={(e) => {
                      const val = e.target.value || null;
                      setFilterDeviceSn(val);
                      if (val && val.startsWith('catalog:')) {
                        // Navigate to catalog device view, keep installationId
                        const catId = val.replace('catalog:', '');
                        const instParam = selectedInstallationId ? `&installationId=${selectedInstallationId}` : '';
                        window.location.href = `/wattel-em?deviceId=${catId}${instParam}`;
                      } else if (val && val.startsWith('point:')) {
                        const pointId = val.replace('point:', '');
                        const inst = installations.find(i => i.id === selectedInstallationId);
                        const eq = inst?.zones.flatMap(z => z.equipment || []).find(e => String(e.id) === pointId);
                        if (eq) navigateToPoint(eq);
                      } else if (val) {
                        // Naviguer vers le WattelEM sélectionné (garde l'installation)
                        const instParam = selectedInstallationId ? `&installationId=${selectedInstallationId}` : '';
                        window.location.href = `/wattel-em?sn=${val}${instParam}`;
                      } else {
                        // Réinitialiser le filtre device, garder l'installation
                        const instParam = selectedInstallationId ? `?installationId=${selectedInstallationId}` : '';
                        window.location.href = `/wattel-em${instParam}`;
                      }
                    }}
                    disabled={!selectedInstallationId}
                    className="w-full text-sm bg-zce-gray-light border border-gray-200 rounded-lg px-3 py-2.5 text-zce-navy font-medium focus:outline-none focus:ring-2 focus:ring-zce-blue/50 focus:border-zce-blue disabled:opacity-50 disabled:cursor-not-allowed transition-all appearance-none cursor-pointer"
                  >
                    <option value="">
                      {selectedInstallationId
                        ? t('eq_allDevices')
                        : t('eq_selectInstallation')}
                    </option>
                    {selectedInstallationId && (() => {
                      const inst = installations.find(i => i.id === selectedInstallationId);
                      if (!inst) return null;
                      // WattelEM SNs (split comma-separated)
                      const wattelemSns = inst.zones
                        .flatMap(z => (z.wattelemSn || '').split(',').map(s => s.trim()).filter(Boolean));
                      // Catalog device IDs from equipment (WattelGo, WattelWall, etc.)
                      const catalogDeviceIds = inst.zones
                        .flatMap(z => z.equipment || [])
                        .map(eq => eq.deviceId)
                        .filter((id): id is number => id != null && id > 0);
                      const uniqueCatalogIds = [...new Set(catalogDeviceIds)];
                      const uniqueSns = [...new Set(wattelemSns)];
                      return (
                        <>
                          {uniqueSns.map(sn => {
                            const dev = devices.find(d => d.device_sn.toLowerCase() === sn.toLowerCase());
                            return (
                              <option key={`em-${sn}`} value={sn}>
                                {displaySn(sn)} {dev ? (dev.online ? `● ${t('eq_online')}` : `○ ${t('eq_offline')}`) : ''}
                              </option>
                            );
                          })}
                          {uniqueCatalogIds.map(id => {
                            const cat = catalogDevices.find(c => c.deviceId === id);
                            return (
                              <option key={`cat-${id}`} value={`catalog:${id}`}>
                                {cat ? (displaySn(cat.deviceName || cat.deviceSn) || t('eq3_device_number', { id })) : t('eq3_device_number', { id })}
                                {cat?.chargePointModel ? ` (${cat.chargePointModel})` : ''}
                              </option>
                            );
                          })}
                          {inst.zones.flatMap(z => z.equipment || [])
                            .filter(eq => !eq.deviceId || eq.deviceId <= 0)
                            .map(eq => {
                              const sn = (eq as any).deviceSn as string | null | undefined;
                              if (sn) {
                                return (
                                  <option key={`mqtt-${eq.id}`} value={sn}>
                                    {displayDefaultName(eq.name, t, tp)} ● MQTT
                                  </option>
                                );
                              }
                              return (
                                <option key={`point-${eq.id}`} value={`point:${eq.id}`}>
                                  {displayDefaultName(eq.name, t, tp)} ● {t('eq_calculated')}
                                </option>
                              );
                            })}
                        </>
                      );
                    })()}
                  </select>
                </div>
              </div>
            )}

            {/* Bouton pour voir tous les devices si on est en mode filtré */}
            {(effectiveSn || filterDeviceSn) && (
              <button
                onClick={() => {
                  const instParam = selectedInstallationId ? `?installationId=${selectedInstallationId}` : '';
                  window.location.href = `/wattel-em${instParam}`;
                }}
                className="w-full text-sm text-zce-blue hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <span>←</span>
                <span>{t('eq_viewAllDevices', { n: installationDeviceSns?.length ?? 0 })}</span>
              </button>
            )}
            {/* Info du device depuis le catalogue (quand venant de l'architecture) */}
            {catalogInfo && (
              <div className="bg-gradient-to-r from-blue-50 to-sky-50 border border-zce-blue/30 rounded-lg p-3 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <img src="/logos/logo zce.png" alt="ZCE" className="h-4 w-auto object-contain" />
                  <span className="font-semibold text-zce-navy">{catalogInfo.deviceName ? displaySn(catalogInfo.deviceName) : t('adm_ota_th_device')}</span>
                </div>
                {catalogInfo.chargePointModel && (
                  <p className="text-xs text-zce-blue">{t('eq_model', { model: catalogInfo.chargePointModel })}</p>
                )}
              </div>
            )}
            {filteredDevices.map((d) => (
              <DeviceCard
                key={d.device_sn}
                device={d}
                selected={d.device_sn === selectedSn}
                onClick={() => selectDevice(d.device_sn)}
              />
            ))}
            {installationCatalogEquipment
              // Si un deviceId est sélectionné dans l'URL, ne montrer que ce device (legacy)
              .filter(eq => !deviceIdFromUrl || String(eq.deviceId) === deviceIdFromUrl)
              // Si un SN est sélectionné dans l'URL :
              // - si l'équipement a ce deviceSn ET un deviceId → c'est un doublon avec filteredDevices, on le masque
              // - si l'équipement a uniquement un deviceId (pas de deviceSn) → on le masque aussi (pas pertinent)
              // En résumé : quand snFromUrl est actif, on masque tous les catalog cards
              .filter(() => !snFromUrl)
              .map((eq) => {
              const cat = eq.deviceId != null ? catalogDevices.find(c => c.deviceId === eq.deviceId) : (eq.deviceSn ? catalogDevices.find(c => c.deviceSn === eq.deviceSn) : undefined);
              const meter = eq.deviceId != null ? installationMeterLatest[eq.deviceId] : undefined;
              // For SN-linked equipment, pull live MQTT telemetry from the WattelEM devices list
              const liveDev = eq.deviceSn ? devices.find(d => d.device_sn.toLowerCase() === (eq.deviceSn as string).toLowerCase()) : undefined;
              // Find linked WattelEM voltage from the same zone (for V/A calculation on chargers)
              const linkedZone = installations.find(i => i.id === selectedInstallationId)?.zones.find(z => (z.equipment || []).some(e => e.id === eq.id || (e.deviceId && e.deviceId === eq.deviceId)));
              const linkedWemVoltage = (() => {
                if (!linkedZone) return null;
                const sns = (linkedZone.wattelemSn || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
                for (const sn of sns) {
                  const wem = devices.find(d => d.device_sn.toLowerCase() === sn);
                  if (wem?.current?.voltage_v && Number(wem.current.voltage_v) > 10) return Number(wem.current.voltage_v);
                }
                return null;
              })();
              const isSelected = eq.deviceId != null
                ? deviceIdFromUrl === String(eq.deviceId)
                : (snFromUrl != null && eq.deviceSn != null && eq.deviceSn.toLowerCase() === snFromUrl.toLowerCase());
              return (
                <CatalogDeviceCard
                  key={`cat-eq-${eq.id ?? eq.deviceId ?? eq.deviceSn}`}
                  equipment={eq}
                  catalogDevice={cat}
                  meterData={meter}
                  wattelemDevice={liveDev}
                  linkedWemVoltage={linkedWemVoltage}
                  selected={isSelected}
                  onClick={() => {
                    const instParam = selectedInstallationId ? `&installationId=${selectedInstallationId}` : '';
                    if (eq.deviceId) {
                      window.location.href = `/wattel-em?deviceId=${eq.deviceId}${instParam}`;
                    } else if (eq.deviceSn) {
                      window.location.href = `/wattel-em?sn=${encodeURIComponent(eq.deviceSn)}${instParam}`;
                    }
                  }}
                />
              );
            })}
            {/* Équipements sans device (points calculés comme Prise 16A) */}
            {!snFromUrl && !deviceIdFromUrl && selectedInstallationId && (() => {
              const inst = installations.find(i => i.id === selectedInstallationId);
              if (!inst) return null;
              return inst.zones
                .flatMap(z => (z.equipment || []).map(eq => ({ ...eq, zoneName: z.name })))
                // Truly unassigned: no legacy deviceId AND no new deviceSn
                .filter(eq => (!eq.deviceId || eq.deviceId <= 0) && !eq.deviceSn)
                .map(eq => {
                  const isSelected = isSimplePointMode && pointNameFromUrl === eq.name;
                  const deduced = eq.id ? computeDeducedForEquipment(eq.id) : { energyWh: 0, powerW: 0 };
                  const powerKw = deduced.powerW / 1000;
                  const energyKwh = deduced.energyWh / 1000;
                  return (
                    <div
                      key={`point-eq-${eq.id}`}
                      onClick={() => navigateToPoint(eq)}
                      className={`w-full text-left p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected ? 'border-pink-500 bg-pink-50' : 'border-gray-200 bg-white hover:border-pink-300 hover:bg-pink-50/50'
                      } shadow-sm`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-zce-navy text-sm truncate">{displayDefaultName(eq.name, t, tp)}</span>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-pink-500"></span>
                          {t('eq_calculated')}
                        </span>
                      </div>
                      {/* Valeurs calculées */}
                      <div className="grid grid-cols-2 gap-3 text-xs mb-2">
                        <div className="bg-orange-50 rounded-lg p-2 text-center">
                          <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">{t('eq_power')}</span>
                          <span className="font-bold text-orange-600">{powerKw > 0 ? powerKw.toFixed(3) : '—'} kW</span>
                        </div>
                        <div className="bg-emerald-50 rounded-lg p-2 text-center">
                          <span className="text-zce-gray-muted block text-[10px] uppercase tracking-wider">{t('eq_energy')}</span>
                          <span className="font-bold text-emerald-600">{energyKwh > 0 ? energyKwh.toFixed(3) : '—'} kWh</span>
                        </div>
                      </div>
                      <div className="text-xs text-zce-gray-muted flex items-center gap-1.5">
                        <span>{getPointIcon(eq.type || null)}</span>
                        <span>{t('eq_calculatedPointNoDevice')}</span>
                      </div>
                    </div>
                  );
                });
            })()}
          </div>

          {/* Right: detail */}
          <div className="lg:col-span-2 space-y-6">
            {selectedDevice ? (
              <>
                <TelemetryGrid device={selectedDevice} />
                {controlLevel === 'admin' && (
                  <ControlModeCard
                    mode={selectedIsMeterOnly ? 'meter_only' : 'controllable'}
                    saving={controlModeSaving}
                    error={controlModeError}
                    onChange={(m) => changeControlMode(selectedDevice.device_sn, m)}
                  />
                )}
                {canControlSelected ? (
                  <RelayControl
                    device={selectedDevice}
                    onRelay={(v) => {
                      // ⚠ Pose le hold AVANT l'envoi : la planification ne pourra
                      // pas écraser ce clic. Reprise auto au prochain événement
                      // planifié (différent de l'état actuel).
                      forceManual(selectedDevice.device_sn, v);
                      sendRelay(selectedDevice.device_sn, v);
                    }}
                    pending={relayPending}
                  />
                ) : selectedIsMeterOnly ? (
                  <MeterOnlyNotice />
                ) : null}
                <ScheduleStatusCard
                  readOnlyReason={scheduleReadOnlyReason}
                  schedule={deviceSchedule}
                  onOpen={() => setScheduleModalOpen(true)}
                  onToggleEnabled={() => deviceSchedule && setDeviceSchedule({ ...deviceSchedule, enabled: !deviceSchedule.enabled, updatedAt: new Date().toISOString() })}
                  onDelete={() => {
                    if (window.confirm(t('eq_confirmDeleteSchedule'))) {
                      setDeviceSchedule(null);
                    }
                  }}
                />
                <ZoneInfo device={selectedDevice} />

                {/* Charts Section */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-zce-navy to-zce-blue flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-zce-navy font-heading">{t('eq_energyReport')}</h2>
                        <p className="text-xs text-zce-gray-muted">{t('eq_energyReportSubtitle')}</p>
                      </div>
                    </div>
                    <ChartTimeFilter value={chartTimeFilter} onChange={setChartTimeFilter} />
                  </div>
                  
                  {/* Energy over time chart */}
                  <EnergyOverTimeChart
                    data={filteredDailyEnergy.length > 0 || filteredEnergyLoading ? filteredDailyEnergy : dailyEnergy}
                    loading={filteredEnergyLoading || loading}
                    title={t('eq_consumptionOf', { name: displaySn(selectedDevice.device_sn) })}
                    hourly={chartTimeFilter.range === '24h'}
                    rangeDays={chartTimeFilter.range === 'custom' ? undefined : chartTimeFilter.days}
                    rangeStartDate={chartTimeFilter.range === 'custom' ? chartTimeFilter.startDate : undefined}
                    rangeEndDate={chartTimeFilter.range === 'custom' ? chartTimeFilter.endDate : undefined}
                  />

                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-16 text-center">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-zce-gray-light to-gray-200 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-zce-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-zce-navy mb-2 font-heading">{t('eq_selectDevice')}</h3>
                <p className="text-zce-gray-muted">{t('eq_selectDeviceHint')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-zce-blue flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="text-sm">
          <p className="font-semibold text-zce-navy">{t('eq_realtimeData')}</p>
          <p className="mt-1 text-zce-gray-muted">
            {t('eq_realtimeDataInfo1')} {t('eq_realtimeDataInfo2')}
          </p>
        </div>
      </div>

      {/* Modal de planification */}
      <SchedulePlannerModal
        open={scheduleModalOpen}
        deviceName={selectedDevice ? (selectedDevice.device_sn || 'WattelEM') : ''}
        initial={deviceSchedule}
        onClose={() => setScheduleModalOpen(false)}
        onSave={(s) => setDeviceSchedule(s)}
      />
    </div>
  );
}

export default WattelEMPage;
